/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Appointments API
 * API for managing appointments (afspraken) within an organization. All operations are scoped to the authenticated user\'s organization.  ## Session Types  The API supports four types of sessions:  ### Individual Sessions Standard one-on-one appointments with a single client and behandelaar. Use `clientId` for single client appointments.  ### Group Sessions (explicit clients) Appointments with multiple clients (e.g., group therapy, family sessions). Use `clientIds` array to specify multiple clients. Each client gets individual attendance tracking and billable quarters calculation.  ### Client Group Sessions (dynamic membership) Appointments linked to a Client Group. The participating clients are determined dynamically based on who is a member of the group on the appointment date. Use `groupId` to assign a client group. For recurring appointments, each instance resolves members independently based on its own `startTime`.  When `groupId` is set, the `clientIds` field is ignored (the group determines clients). The response includes: - `group`: Basic group info (id, name) - `participants`: Clients who are members of the group on the appointment date   (each entry carries `clientId`, `clientName`, and membership dates)  ### Multidisciplinary Sessions Appointments with multiple behandelaren (e.g., MDO - multidisciplinair overleg). Use `attendeeIds` array to add additional behandelaren beyond the primary `behandelaarId` (who becomes the organizer).  ### Internal Meetings Team meetings without clients. Use `category: \"INTERNAL\"` and omit client fields.  ## All-Day Appointments  All-day appointments (e.g., daily stay costs in residential care) span the entire day. Set `isAllDay: true` to create an all-day appointment. When `isAllDay` is true: - `startTime` should be set to the start of the day (00:00 / midnight) - `endTime` should be set to the end of the same day (23:59) - `durationMinutes` reads 1439 — the span is 00:00:00.000 through 23:59:59.999,   and the response derives the duration from those two times rather than storing it  All-day appointments are backwards compatible: existing appointments default to `isAllDay: false`. Note: multi-day appointments (spanning multiple days) are not currently supported.  ## Billable Quarters  For group sessions, each client\'s attendance is tracked individually: - `scheduledStartTime` / `scheduledEndTime` - copied from appointment - `actualStartTime` / `actualEndTime` - set during attendance registration - `billableQuarters` - auto-calculated as ceil(actualMinutes / 15)  Integrates with FullCalendar for calendar UI (see ADR-006). 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * A dagnotitie: text on a day in the agenda (#4063). Organisation-wide — everyone in the practice sees the same note on that date — and deliberately carrying nothing else: no cliënt, no behandelaar, no afspraaktype and no duration. 
 */
export interface AgendaDayNoteResponse {
    'id': string;
    /**
     * The day this note is about
     */
    'date': string;
    /**
     * The note itself
     */
    'text': string;
    /**
     * Epoch milliseconds
     */
    'createdAt': number;
    /**
     * Epoch milliseconds
     */
    'updatedAt': number;
}
/**
 * A medewerker (behandelaar) participating in an appointment. Mirrors AppointmentParticipant on the client side: each entry carries a real userId so the frontend can resolve the roster to the IDs it needs for behandelaarId + attendeeIds. 
 */
export interface AppointmentAttendeeRef {
    /**
     * User ID of the medewerker
     */
    'userId': string;
    /**
     * Full name of the medewerker
     */
    'userName': string;
    /**
     * NZA profession code of the medewerker, used to validate qualification for specific appointment types. Valid values: ARTS_BIG3, ARTS_BIG14, GZ_PSYCH, KLIN_PSYCH, PSYCHOTHER, VERPL, VERPL_SPEC, OVERIG 
     */
    'professionCode'?: string | null;
    /**
     * Whether this medewerker is the organizer (the primary behandelaar). Exactly one attendee per appointment has isOrganizer set to true. 
     */
    'isOrganizer': boolean;
}
/**
 * One behandelaar taking part in a client\'s afspraken, as the behandelaar filter of the dossier\'s Afspraken tab offers them (#4373).
 */
export interface AppointmentBehandelaarResponse {
    'userId': string;
    'userName': string;
}
/**
 * Authorization capabilities for an appointment
 */
export interface AppointmentCapabilitiesResponse {
    /**
     * Whether the user can view this appointment
     */
    'view': boolean;
    /**
     * Whether the user can update this appointment
     */
    'update': boolean;
    /**
     * Whether the user can edit the interne notitie and the dossiernotitie of this appointment. Unlike `update`, this stays true after the appointment is completed or cancelled (#3309) — notes are always editable for the organizer, an assigned behandelaar, or holders of appointments.update.any.
     */
    'updateNotes': boolean;
    /**
     * Whether the user can register per-client attendance (#3317). Like updateNotes, stays true after completion — attendance is registered right after the session.
     */
    'updateAttendance': boolean;
    /**
     * Whether the user can cancel this appointment
     */
    'cancel': boolean;
    /**
     * Whether the user can delete this appointment. True only when the caller has the delete permission (or delete-own and owns the appointment) AND the appointment has no billing/invoice state that would block deletion.
     */
    'delete': boolean;
    /**
     * Whether the user can mark this appointment as completed
     */
    'markCompleted': boolean;
    /**
     * Whether the user can mark this appointment as no-show
     */
    'markNoshow': boolean;
    /**
     * Whether the user can revert this appointment from a terminal state back to scheduled
     */
    'revert': boolean;
    /**
     * Whether the user can add attendees to this appointment
     */
    'addAttendee': boolean;
    /**
     * Whether the user can start recording for this appointment
     */
    'startRecording': boolean;
}
/**
 * Appointment category: - CLIENT: Client appointment - PRIVATE: Private/personal time - INTERNAL: Internal meeting 
 */

export const AppointmentCategory = {
    Client: 'CLIENT',
    Private: 'PRIVATE',
    Internal: 'INTERNAL'
} as const;

export type AppointmentCategory = typeof AppointmentCategory[keyof typeof AppointmentCategory];


/**
 * Bevestiging of a planned afspraak, beside its status rather than a value of it — a reserved afspraak is still SCHEDULED: - RESERVED: Slot held, nobody invited yet (reservering) - INVITED: Invited, waiting for an answer (uitgenodigd) - CONFIRMED: The invitation was accepted (bevestigd) 
 */

export const AppointmentConfirmation = {
    Reserved: 'RESERVED',
    Invited: 'INVITED',
    Confirmed: 'CONFIRMED'
} as const;

export type AppointmentConfirmation = typeof AppointmentConfirmation[keyof typeof AppointmentConfirmation];


export interface AppointmentDossierNoteResponse {
    /**
     * The dossiernotitie, or null when the afspraak has none.
     */
    'note': string | null;
}
/**
 * Reference to a client group assigned to an appointment
 */
export interface AppointmentGroupRef {
    /**
     * Client group ID
     */
    'id': string;
    /**
     * Client group name
     */
    'name': string;
}
export interface AppointmentListResponse {
    /**
     * List of appointments
     */
    'content': Array<AppointmentResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of appointments matching criteria
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * A client participating in an appointment. Populated identically for all session types (single client, explicit clientIds, dynamic groupId). Membership dates are only populated for dynamic-group sessions; they are null for single/explicit-clientIds sessions where no group membership applies. 
 */
export interface AppointmentParticipant {
    'outcome'?: ParticipantOutcome;
    /**
     * Whether the client attended; derived from `outcome` (#3317,
     */
    'isPresent'?: boolean;
    /**
     * Registered actual arrival (epoch ms), when it deviates from the schedule
     */
    'actualStartTime'?: number | null;
    /**
     * Registered actual departure (epoch ms), when it deviates from the schedule
     */
    'actualEndTime'?: number | null;
    /**
     * Derived attended minutes (database-computed)
     */
    'actualMinutes'?: number | null;
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName': string;
    /**
     * Group membership start date (ISO 8601), only populated for dynamic-group sessions. Null for single-client and explicit-clientIds sessions. 
     */
    'membershipStartDate'?: string | null;
    /**
     * Group membership end date (ISO 8601), only populated for dynamic-group sessions. Null if ongoing, or for single-client and explicit-clientIds sessions. 
     */
    'membershipEndDate'?: string | null;
    /**
     * Name of the client\'s currently active zorgtraject (its behandelsetting), mirroring `ClientProfile.activeZorgtrajectName`. Read-only enrichment so the afspraak-composer can show the same zorgtraject subtitle in edit mode as it does for a freshly searched client (#3093). Null when the client has no active zorgtraject, and always when `activeZorgtrajectFinancingRegime` is set and is not `ZVW` (#4787). 
     */
    'activeZorgtrajectName'?: string | null;
    /**
     * Financing regime of the client\'s currently active zorgtraject, mirroring `ClientProfile.activeZorgtrajectFinancingRegime`. A regime other than `ZVW` has no behandelsetting, so the afspraak-composer names that zorgtraject by its regime. 
     */
    'activeZorgtrajectFinancingRegime'?: FinancingRegime | null;
    /**
     * Level of this client\'s standing warning, mirroring `ClientProfile.warningLevel`. Read-only enrichment so opening an appointment surfaces the warning without a second request per participant (#4567). Null when the client has none. 
     */
    'warningLevel'?: ClientWarningLevel | null;
    /**
     * This client\'s standing warning, mirroring `ClientProfile.warningText`. Always null together with `warningLevel`. It follows the client rather than the appointment, so the same text appears on every appointment they take part in. 
     */
    'warningText'?: string | null;
}


export interface AppointmentResponse {
    /**
     * Appointment unique identifier
     */
    'id': string;
    /**
     * Organization this appointment belongs to
     */
    'organizationId': string;
    /**
     * Client ID (null for internal/private appointments)
     */
    'clientId'?: string | null;
    /**
     * Client full name (denormalized for display)
     */
    'clientName'?: string | null;
    /**
     * Appointment type ID
     */
    'appointmentTypeId'?: string | null;
    /**
     * Appointment type name (denormalized for display)
     */
    'appointmentTypeName'?: string | null;
    /**
     * Accent colour of the appointment type as a `#RRGGBB` hex string (denormalized for display, #3270). The agenda renders it as a left accent strip; null means the event renders without an accent. 
     */
    'appointmentTypeColor'?: string | null;
    /**
     * MDO (multidisciplinair overleg): the clients on this appointment are the subjects discussed, not invitees (#3304). Snapshotted from the appointment type at creation, so later type edits don\'t change it. 
     */
    'isMdo'?: boolean;
    /**
     * Appointment title
     */
    'title': string;
    /**
     * Appointment description/notes
     */
    'description'?: string | null;
    /**
     * Internal planning/handover note. Rendered in the agenda and the appointment composer only — never in the client dossier or any client-facing view (#3104). 
     */
    'internalNote'?: string | null;
    /**
     * Start time (milliseconds since epoch)
     */
    'startTime': number;
    /**
     * End time (milliseconds since epoch)
     */
    'endTime': number;
    /**
     * Duration in minutes, derived from startTime and endTime — never stored, so a resize is reflected here immediately (#3944). An all-day appointment reads 1439 (the span is 00:00:00.000 through 23:59:59.999).  The bound is 0 rather than 1 because the value is now truncated from the two times: only `endTime > startTime` is enforced, so an appointment shorter than a minute reads 0. A stored duration could claim 1 for the same appointment. 
     */
    'durationMinutes': number;
    'category': AppointmentCategory;
    'locationType': LocationType;
    /**
     * Physical location ID
     */
    'locationId'?: string | null;
    /**
     * Location name (denormalized for display)
     */
    'locationName'?: string | null;
    /**
     * Room ID
     */
    'roomId'?: string | null;
    /**
     * Room name (denormalized for display)
     */
    'roomName'?: string | null;
    /**
     * URL for online meetings
     */
    'onlineMeetingUrl'?: string | null;
    /**
     * Primary behandelaar (organizer) user ID
     */
    'behandelaarId': string;
    /**
     * Behandelaar full name (denormalized for display)
     */
    'behandelaarName'?: string;
    'status': AppointmentStatus;
    /**
     * Bevestiging of the afspraak, or null where the practice does not track one. Set through `setAppointmentConfirmation` while SCHEDULED; after a cancellation, completion or no-show the stored value is still returned but no longer meaningful. 
     */
    'confirmation'?: AppointmentConfirmation | null;
    /**
     * Reason for cancellation (if cancelled)
     */
    'cancellationReason'?: string | null;
    /**
     * Cancellation timestamp (milliseconds since epoch)
     */
    'cancelledAt'?: number | null;
    /**
     * Whether the insurer or the cliënt already has every declarabele consult on this afspraak — what the agenda draws a euroteken for instead of the afgerond-vinkje (#4398).  Derived, never stored, and derived from the *same* relations the declaratiequeue reads: `settled_consult_v` (the single definition of \"already declared or invoiced\", #3321/#4113) says something on this afspraak has gone out, and an empty `billable_consult_v` says nothing on it is still openstaand. A consult is per appointment-cliënt, so a partly settled groepssessie reads `false` — the weaker claim is the safe one.  Always `false` for an afspraak that is not `COMPLETED`: nothing is declarable before it is afgerond, and a declaration released for herdeclaratie is openstaand work again, which is what turns this back to `false`. 
     */
    'isSettled'?: boolean;
    /**
     * Which behandelaren\'s time on this afspraak is declared — the same euroteken as `isSettled`, per kolom (#4504). A groepssessie led by two behandelaren where only the organiser\'s time went out (#4498) is exactly the case the whole-afspraak flag cannot state: nothing is openstaand, so `isSettled` is `true` for a session the co-behandelaar never declared.  Derived, never stored, and derived from the *same* relation the declaratiequeue reads: `settled_consult_v`, the single definition of \"already declared or invoiced\" (#3321/#4113), which names the behandelaar each declared line was written for. A behandelaar is listed when every cliënt with a declared consult on this afspraak also has a declared line naming them, and at least one such line exists. The comparison is between the behandelaren on the afspraak, not against what is still openstaand — a cliënt nobody declared is `isSettled`\'s answer, not this field\'s.  Empty for an afspraak that is not `COMPLETED` and for one with nothing declared, for the same reasons `isSettled` is `false` there. This refines `isSettled`; it does not replace it, and a reader wanting \"the payer has this whole afspraak\" still asks that flag. 
     */
    'settledBehandelaarIds'?: Array<string>;
    /**
     * Where the declaration of this afspraak stands — what the euroteken is a claim *about* (#4733). `isSettled` says the payer has the afspraak; it does not say what the payer answered, and a practice read the euroteken as \"betaald\" for consults that were only batched or had been refused.  The least advanced of the afspraak\'s declarable regels, in `DeclarationState`\'s order, derived from the same regels the afspraak\'s declaratiehistorie lists. Absent for an afspraak that is not `COMPLETED` and for one with no regel. 
     */
    'declarationState'?: DeclarationState | null;
    /**
     * How many distinct batches this afspraak\'s regels went out in — every batch the afspraak\'s declaratiehistorie lists, counted once however many of its regels it carried. A regel refused and declared again, or a correction declared later, adds the later batch.  The history says nothing `declarationState` does not until this is above 1, which is when the agenda offers it. `0` for an afspraak that is not `COMPLETED` and for one no batch has declared. 
     */
    'declarationBatchCount'?: number;
    /**
     * Recurrence pattern for this appointment series. Only present on series parent appointments (the template). Instance appointments do not have this field set. 
     */
    'recurrencePattern'?: RecurrencePattern | null;
    /**
     * ID of the series this appointment belongs to. For series parent: equals the appointment\'s own ID. For instances: references the parent appointment ID. Null for non-recurring appointments. 
     */
    'seriesId'?: string | null;
    /**
     * Whether this appointment is part of a recurring series. True for both series parents and instances. 
     */
    'isRecurring'?: boolean;
    /**
     * Number of attendees (behandelaren) in this appointment
     */
    'attendeeCount'?: number;
    /**
     * Names of all attendees (behandelaren) in this appointment
     */
    'attendeeNames'?: Array<string>;
    /**
     * Canonical list of participating medewerkers (behandelaren), each entry carrying a real userId. Mirrors the client-side `participants` list. Includes the organizer (the primary behandelaar) with exactly one entry flagged `isOrganizer: true`, so the afspraak-composer edit mode can prefill both `behandelaarId` and `attendeeIds`. 
     */
    'attendees'?: Array<AppointmentAttendeeRef>;
    /**
     * Client group ID. When set, the appointment\'s clients are determined by who is a member of this group on the appointment date. If groupId is set, clientIds are ignored. 
     */
    'groupId'?: string | null;
    /**
     * Basic group information. Only populated when groupId is set. 
     */
    'group'?: AppointmentGroupRef | null;
    /**
     * Canonical list of participating clients, populated identically for all session types: single client, explicit clientIds, and dynamic groupId. Each entry carries a real clientId. For dynamic groups, the list reflects group membership resolved on the appointment date (with membership dates). Empty for appointments without clients (e.g. internal/private appointments). 
     */
    'participants'?: Array<AppointmentParticipant>;
    /**
     * Whether this is an all-day appointment (e.g., daily stay costs in residential care). All-day appointments span from 00:00 to 23:59 of the given date. For all-day appointments, startTime is set to the start of the day and endTime to the end of the same day (23:59:59.999), so the derived durationMinutes reads 1439. 
     */
    'isAllDay'?: boolean;
    /**
     * Whether this appointment blocks the behandelaar\'s availability for scheduling. When true (default), other appointments cannot be booked in the same time slot. When false, this appointment does not block the calendar (useful for study days, internal meetings, administrative time blocks). 
     */
    'blocksAvailability'?: boolean;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). Reserved around the session in the agenda and counted as busy by availability; never part of startTime/endTime, so never declared. 
     */
    'indirectMinutesBefore'?: number;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). Reserved around the session in the agenda and counted as busy by availability; never part of startTime/endTime, so never declared. 
     */
    'indirectMinutesAfter'?: number;
    /**
     * Travel time before the session, in minutes (#4817). Reserved around the session in the agenda and counted as busy by availability; never part of startTime/endTime, so never declared. 
     */
    'travelMinutesBefore'?: number;
    /**
     * Travel time after the session, in minutes (#4817). Reserved around the session in the agenda and counted as busy by availability; never part of startTime/endTime, so never declared. 
     */
    'travelMinutesAfter'?: number;
    /**
     * Derived, not stored (#4788): the zorgtraject of the appointment\'s one client that covers the appointment\'s date, closed or not — the trajectory the consult is declared under. Null for a group or multi-client session, and when no trajectory of the client covers the date. 
     */
    'zorgtrajectId'?: string | null;
    /**
     * Display name of the zorgtraject in `zorgtrajectId`; derived the same way, null when it is.
     */
    'zorgtrajectName'?: string | null;
    /**
     * Soft, non-blocking warning returned on create/update when the appointment falls outside the behandelaar\'s working hours or during an availability exception (absence). The appointment is still created/updated regardless; this only nudges the planner (ADR-015 \"warn, don\'t block\", #2933). 
     */
    'availabilityWarning'?: string | null;
    /**
     * User ID of who created the appointment (may differ from behandelaarId when created on behalf of another)
     */
    'createdById'?: string | null;
    /**
     * Full name of who created the appointment (denormalized for display)
     */
    'createdByName'?: string | null;
    /**
     * Timestamp when appointment was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when appointment was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * Response containing appointments in a recurring series
 */
export interface AppointmentSeriesResponse {
    /**
     * ID of the series (same as parent appointment ID)
     */
    'seriesId': string;
    'parent'?: AppointmentResponse;
    'recurrencePattern'?: RecurrencePattern;
    /**
     * List of appointment instances in the series. Ordered by startTime ascending. Includes both generated instances and exceptions. 
     */
    'instances': Array<AppointmentResponse>;
    /**
     * Total number of instances in the series (including those outside requested range)
     */
    'totalCount': number;
    /**
     * Whether there are more instances beyond the requested date range
     */
    'hasMore'?: boolean;
}
/**
 * Appointment status: - SCHEDULED: Planned/scheduled - CANCELLED: Cancelled (generic) - CANCELLED_ADVANCE: Cancelled at least the organisation\'s late-cancellation window (default 24 hours) in advance - CANCELLED_LATE: Cancelled within the organisation\'s late-cancellation window (default 24 hours) - NO_SHOW: Client didn\'t show up - COMPLETED: Appointment completed 
 */

export const AppointmentStatus = {
    Scheduled: 'SCHEDULED',
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE',
    NoShow: 'NO_SHOW',
    Completed: 'COMPLETED'
} as const;

export type AppointmentStatus = typeof AppointmentStatus[keyof typeof AppointmentStatus];


export interface CancelAppointmentRequest {
    /**
     * Reason for cancellation
     */
    'reason'?: string | null;
    'cancellationStatus'?: CancellationStatus;
    /**
     * Answer to the \"vervangen door een andere afspraak?\" question (#3438). Only meaningful when the appointment\'s type has cancellation replacement enabled for the resulting cancellation status; requesting it otherwise is a 400. Omitted or false cancels exactly as before, leaving nothing behind. 
     */
    'createReplacement'?: boolean | null;
}


/**
 * Request to cancel a recurring appointment series from a specific date
 */
export interface CancelSeriesRequest {
    /**
     * Cancel all instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
     */
    'fromDate': string;
    /**
     * Optional reason for cancellation (applies to all cancelled instances)
     */
    'reason'?: string | null;
}
/**
 * Response after cancelling series instances
 */
export interface CancelSeriesResponse {
    /**
     * ID of the series parent
     */
    'seriesId': string;
    /**
     * Number of instances cancelled
     */
    'cancelledCount': number;
    /**
     * Number of instances that remain unchanged (before fromDate)
     */
    'unchangedCount': number;
    /**
     * Number of instances skipped (already cancelled or completed)
     */
    'skippedCount'?: number;
    /**
     * The cancellation reason applied to all cancelled instances
     */
    'reason'?: string | null;
}
/**
 * Explicit classification of a cancellation, chosen by the behandelaar. Omit to derive the status from the cancellation timing. - CANCELLED: Cancelled (generic, e.g. at or after the start time) - CANCELLED_ADVANCE: Timely cancellation (at least the organisation\'s late-cancellation window (default 24 hours) before start) - CANCELLED_LATE: Late cancellation (within the organisation\'s late-cancellation window (default 24 hours) of the start) 
 */

export const CancellationStatus = {
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE'
} as const;

export type CancellationStatus = typeof CancellationStatus[keyof typeof CancellationStatus];


/**
 * How loudly a client\'s standing warning is drawn. INFO is something to know, WARNING something to watch, CRITICAL something that changes what you do before you open the door. Defined identically in openapi-clients.yaml, which owns the write side; this copy exists because AppointmentParticipant carries the warning through and each spec generates its own frontend client.
 */

export const ClientWarningLevel = {
    Info: 'INFO',
    Warning: 'WARNING',
    Critical: 'CRITICAL'
} as const;

export type ClientWarningLevel = typeof ClientWarningLevel[keyof typeof ClientWarningLevel];


export interface CreateAgendaDayNoteRequest {
    /**
     * The day this note is about
     */
    'date': string;
    /**
     * The note itself
     */
    'text': string;
}
export interface CreateAppointmentRequest {
    /**
     * Client ID (required for CLIENT category, for single-client appointments).  One of clientId, clientIds or groupId is required when the chosen appointmentTypeId has billingCategory INSURED or PRIVATE — see appointmentTypeId. 
     */
    'clientId'?: string | null;
    /**
     * Client IDs for group sessions (multiple clients). If provided, clientId is ignored. Ignored when groupId is set.  One of clientId, clientIds or groupId is required when the chosen appointmentTypeId has billingCategory INSURED or PRIVATE — see appointmentTypeId. 
     */
    'clientIds'?: Array<string> | null;
    /**
     * Client group ID. When set, the appointment\'s clients are determined by who is a member of this group on the appointment date. If groupId is set, clientId and clientIds fields are ignored (the group determines clients).  A groupId satisfies the client requirement of a declarable appointmentTypeId only when it resolves to at least one member on the appointment date. 
     */
    'groupId'?: string | null;
    /**
     * Additional attendee (behandelaar) IDs for multidisciplinary sessions. Primary behandelaarId is automatically included as organizer.
     */
    'attendeeIds'?: Array<string> | null;
    /**
     * Appointment type ID.  OpenAPI 3.0 cannot express the conditional, so it is stated here: an appointment whose type has billingCategory INSURED or PRIVATE must name at least one client, through clientId, clientIds, or a groupId that resolves to members on the appointment date (#4397). It represents direct client time, and one that names nobody is care that can never be declared — billable_consult_v requires client_id IS NOT NULL, so it never reaches the declaration queue and nothing reports it.  A NON_BILLABLE type (internal meetings, intervision, administration) legitimately names nobody, and an appointment with no type at all is out of scope of the rule. Violations are rejected with 400 and a Problem Details `errors` entry on `clientIds`. 
     */
    'appointmentTypeId'?: string | null;
    /**
     * Appointment title
     */
    'title': string;
    /**
     * Appointment description/notes
     */
    'description'?: string | null;
    /**
     * Internal planning/handover note. Rendered in the agenda and the appointment composer only — never in the client dossier or any client-facing view (#3104). 
     */
    'internalNote'?: string | null;
    /**
     * Start time (milliseconds since epoch)
     */
    'startTime': number;
    /**
     * End time (milliseconds since epoch)
     */
    'endTime': number;
    /**
     * Deprecated and ignored — the duration is derived from startTime and endTime (#3944). Sending it is harmless; it is not read. The frontend stopped sending it in #4917; it leaves the contract once no open tab still runs a build that does, because an unknown property is refused. 
     * @deprecated
     */
    'durationMinutes'?: number;
    'category'?: AppointmentCategory;
    'locationType'?: LocationType;
    /**
     * Physical location ID
     */
    'locationId'?: string | null;
    /**
     * Room ID
     */
    'roomId'?: string | null;
    /**
     * URL for online meetings
     */
    'onlineMeetingUrl'?: string | null;
    /**
     * Primary behandelaar user ID (defaults to current user)
     */
    'behandelaarId'?: string | null;
    /**
     * Bevestiging to plan the afspraak with, e.g. RESERVED for a held slot. Omitted or null means none. A recurring series\' occurrences inherit it. 
     */
    'confirmation'?: AppointmentConfirmation | null;
    /**
     * Recurrence pattern for recurring appointments. When provided, creates a recurring appointment series. The created appointment becomes the series parent (template). 
     */
    'recurrence'?: RecurrencePattern | null;
    /**
     * Whether this is an all-day appointment (e.g., daily stay costs in residential care). When true, the appointment spans the entire day (00:00-23:59). For all-day appointments, startTime should be the start of the day (midnight) and endTime should be end of the same day. Duration is implied (full day = 1440 minutes). 
     */
    'isAllDay'?: boolean;
    /**
     * Whether this appointment blocks the behandelaar\'s availability for scheduling. When true (default), other appointments cannot be booked in the same time slot for this behandelaar. When false, this appointment does not block the calendar (useful for study days, internal meetings, administrative time blocks). Note: Room availability is always blocked when a room is assigned, regardless of this setting. 
     */
    'blocksAvailability'?: boolean;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). Omitted or null takes the appointment type\'s default (0 without a type). 
     */
    'indirectMinutesBefore'?: number | null;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). Omitted or null takes the appointment type\'s default (0 without a type). 
     */
    'indirectMinutesAfter'?: number | null;
    /**
     * Travel time before the session, in minutes (#4817). Omitted or null takes the appointment type\'s default (0 without a type). 
     */
    'travelMinutesBefore'?: number | null;
    /**
     * Travel time after the session, in minutes (#4817). Omitted or null takes the appointment type\'s default (0 without a type). 
     */
    'travelMinutesAfter'?: number | null;
}


/**
 * Day of the week for weekly recurrence patterns
 */

export const DayOfWeek = {
    Monday: 'MONDAY',
    Tuesday: 'TUESDAY',
    Wednesday: 'WEDNESDAY',
    Thursday: 'THURSDAY',
    Friday: 'FRIDAY',
    Saturday: 'SATURDAY',
    Sunday: 'SUNDAY'
} as const;

export type DayOfWeek = typeof DayOfWeek[keyof typeof DayOfWeek];


/**
 * Where a regel\'s declaration stands — or, for an afspraak, the least advanced of its regels, in this order (#4733). Derived on every read, never stored.  - `REJECTED` — the insurer refused it, and it has not been released for redeclaration - `NOT_DECLARED` — on no batch or nota: openstaand, or a replacement waiting to go out - `IN_BATCH` — on a batch that has not been sent - `DECLARED` — sent, and no retour has judged it yet - `PARTIALLY_APPROVED` — granted for less than declared, shortfall still open - `WRITTEN_OFF` — granted for less, and the shortfall was written off - `CREDITED` — reversed in full, with nothing declared in its place - `INVOICED` — a particuliere regel on a nota to the cliënt - `APPROVED` — granted in full 
 */

export const DeclarationState = {
    Rejected: 'REJECTED',
    NotDeclared: 'NOT_DECLARED',
    InBatch: 'IN_BATCH',
    Declared: 'DECLARED',
    PartiallyApproved: 'PARTIALLY_APPROVED',
    WrittenOff: 'WRITTEN_OFF',
    Credited: 'CREDITED',
    Invoiced: 'INVOICED',
    Approved: 'APPROVED'
} as const;

export type DeclarationState = typeof DeclarationState[keyof typeof DeclarationState];


/**
 * Response after deleting series instances
 */
export interface DeleteSeriesResponse {
    /**
     * ID of the series
     */
    'seriesId': string;
    /**
     * Number of instances deleted
     */
    'deletedCount': number;
    /**
     * Number of instances remaining in the series (before fromDate)
     */
    'remainingCount': number;
    /**
     * Whether the entire series was deleted (including parent)
     */
    'seriesDeleted'?: boolean;
}
/**
 * Financing regime (wettelijk kader) under which care is funded and declared
 */

export const FinancingRegime = {
    Zvw: 'ZVW',
    Wmo: 'WMO',
    Jeugdwet: 'JEUGDWET',
    Particulier: 'PARTICULIER',
    Pgb: 'PGB',
    Anders: 'ANDERS'
} as const;

export type FinancingRegime = typeof FinancingRegime[keyof typeof FinancingRegime];


/**
 * Location type: - PHYSICAL: In-person at physical location - ONLINE: Video call/online meeting - NOT_APPLICABLE: Location not applicable 
 */

export const LocationType = {
    Physical: 'PHYSICAL',
    Online: 'ONLINE',
    NotApplicable: 'NOT_APPLICABLE'
} as const;

export type LocationType = typeof LocationType[keyof typeof LocationType];


export interface NoShowAppointmentRequest {
    /**
     * Reason or notes about the no-show
     */
    'reason'?: string | null;
    /**
     * Answer to the \"vervangen door een andere afspraak?\" question (#3635). Only meaningful when the appointment\'s type lists `NO_SHOW` among its `cancellationReplacementTriggers`; requesting it otherwise is a 400. Omitted or false marks the no-show exactly as before, leaving nothing behind. 
     */
    'createReplacement'?: boolean | null;
}
/**
 * An organization member selectable for an appointment or agenda column
 */
export interface OrganizationMemberOption {
    /**
     * User ID of the member
     */
    'id': string;
    /**
     * Full name of the member
     */
    'displayName': string;
    /**
     * Whether this member is designated as a behandelaar in this organization. Administrative staff are selectable too (#3086), so this only lets the UI distinguish them — it is not a filter. 
     */
    'isBehandelaar': boolean;
    /**
     * The profession code of the member, used to validate if they are qualified for specific appointment types. Valid values: ARTS_BIG3, ARTS_BIG14, GZ_PSYCH, KLIN_PSYCH, PSYCHOTHER, VERPL, VERPL_SPEC, OVERIG 
     */
    'professionCode'?: string | null;
}
/**
 * What happened to one participant of an appointment (#3835). The same four outcomes the afmeld-dialoog offers for a whole session, plus PRESENT, recorded per client so a group session can hold different answers for different people.  `isPresent` is derived from it: present if and only if PRESENT. 
 */

export const ParticipantOutcome = {
    Present: 'PRESENT',
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE',
    NoShow: 'NO_SHOW'
} as const;

export type ParticipantOutcome = typeof ParticipantOutcome[keyof typeof ParticipantOutcome];


/**
 * What the behandelaar recorded for one participant (#3835). CANCELLED is refined into one of the three cancellation statuses by `cancellationStatus`, or — when that is omitted — from the timing of the participant\'s own scheduled start, the same rule that classifies a cancellation of the whole appointment. 
 */

export const ParticipantOutcomeChoice = {
    Present: 'PRESENT',
    Cancelled: 'CANCELLED',
    NoShow: 'NO_SHOW'
} as const;

export type ParticipantOutcomeChoice = typeof ParticipantOutcomeChoice[keyof typeof ParticipantOutcomeChoice];


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Recurrence frequency for recurring appointments: - DAILY: Repeats every N days - WEEKLY: Repeats every N weeks on specified days - MONTHLY: Repeats every N months 
 */

export const RecurrenceFrequency = {
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY'
} as const;

export type RecurrenceFrequency = typeof RecurrenceFrequency[keyof typeof RecurrenceFrequency];


/**
 * Defines a recurrence pattern for recurring appointments. Follows simplified iCalendar RRULE concepts. 
 */
export interface RecurrencePattern {
    'frequency': RecurrenceFrequency;
    /**
     * Interval between occurrences (e.g., every 2 weeks). Defaults to 1 (every day/week/month). 
     */
    'interval'?: number;
    /**
     * Days of the week for WEEKLY frequency. Should contain at least one day when frequency is WEEKLY. If omitted or empty for WEEKLY, defaults to the appointment\'s starting day. Ignored for DAILY and MONTHLY frequencies. 
     */
    'daysOfWeek'?: Array<DayOfWeek>;
    /**
     * Date when the recurrence stops (inclusive). Either endDate or occurrences should be provided, not both. If neither is provided, recurrence continues indefinitely (max 1 year). 
     */
    'endDate'?: string | null;
    /**
     * Maximum number of occurrences to generate. Either endDate or occurrences should be provided, not both. 
     */
    'occurrences'?: number | null;
}


/**
 * The whole bevestiging, replaced. Not `required`: a required property cannot carry an explicit null through bean validation, which unwraps the nullable before `@NotNull` sees it — and null is how it is cleared. 
 */
export interface SetAppointmentConfirmationRequest {
    /**
     * The new bevestiging; null or omitted clears it.
     */
    'confirmation'?: AppointmentConfirmation | null;
}


export interface UpdateAgendaDayNoteRequest {
    /**
     * The day this note is about
     */
    'date': string;
    /**
     * The note itself
     */
    'text': string;
}
export interface UpdateAppointmentDossierNoteRequest {
    /**
     * The new dossiernotitie; blank or null removes it.
     */
    'note': string | null;
}
/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateAppointmentRequest {
    /**
     * Full-replacement client selection for this appointment. When provided, the appointment\'s clients are reconciled to exactly this set: new clients are added and absent clients are removed. Removing a client is refused (422) when a billing line or a reviewed form submission already exists for that client on this appointment. Ignored when the appointment is (or becomes) group-driven via groupId.  Emptying it is rejected (400) when the appointment\'s type is declarable — see appointmentTypeId. 
     */
    'clientIds'?: Array<string> | null;
    /**
     * Client group ID. When set, the appointment\'s clients are determined by who is a member of this group on the appointment date. Setting this to null removes the group assignment and reverts to explicit client selection.  A group that resolves to no member on the appointment date is rejected (400) when the appointment\'s type is declarable — see appointmentTypeId. 
     */
    'groupId'?: string | null;
    /**
     * Appointment type ID.  The client requirement CreateAppointmentRequest.appointmentTypeId describes (#4397) holds here too, against the state the appointment will be in after the write: a type with billingCategory INSURED or PRIVATE must name at least one client.  It is asked **only of an update that states appointmentTypeId, clientIds or groupId**. Appointments booked before the rule existed are deliberately left alone — there is no honest way to decide who their client was — so an update touching none of the three (a title change, a move, a note) is never refused on their behalf. An update that does state one is validated whole, so the rule cannot be escaped by changing the type of a client-less appointment or by removing its last client. 
     */
    'appointmentTypeId'?: string | null;
    /**
     * Appointment title
     */
    'title'?: string | null;
    /**
     * Appointment description/notes.  Omitting this field leaves the current value untouched; sending it empty (an explicit null, or a blank string) clears it (#3172). 
     */
    'description'?: string | null;
    /**
     * Internal planning/handover note. Rendered in the agenda and the appointment composer only — never in the client dossier or any client-facing view (#3104).  Omitting this field leaves the current value untouched; sending it empty (an explicit null, or a blank string) clears it (#3172). 
     */
    'internalNote'?: string | null;
    /**
     * Start time (milliseconds since epoch)
     */
    'startTime'?: number | null;
    /**
     * End time (milliseconds since epoch)
     */
    'endTime'?: number | null;
    /**
     * Deprecated and ignored — the duration is derived from startTime and endTime (#3944). Resizing an appointment means sending the two times. The frontend stopped sending it in #4917; it leaves the contract once no open tab still runs a build that does, because an unknown property is refused. 
     * @deprecated
     */
    'durationMinutes'?: number | null;
    'category'?: AppointmentCategory;
    'locationType'?: LocationType;
    /**
     * Physical location ID
     */
    'locationId'?: string | null;
    /**
     * Room ID
     */
    'roomId'?: string | null;
    /**
     * URL for online meetings.  Omitting this field leaves the current value untouched; sending an explicit null clears it. An empty string is not a valid clear here — a `format: uri` field has no empty spelling that survives deserialization (#3172). 
     */
    'onlineMeetingUrl'?: string | null;
    /**
     * Primary behandelaar user ID
     */
    'behandelaarId'?: string | null;
    /**
     * Full-replacement set of non-organizer attendee (behandelaar) IDs for this appointment instance. Attendees missing from this list are removed and new IDs are added. The organizer (behandelaarId) is always retained, even when absent from this list. Absent or null leaves the attendee roster unchanged. 
     */
    'attendeeIds'?: Array<string> | null;
    /**
     * Whether this is an all-day appointment. When set to true, the appointment spans the entire day (00:00-23:59). When set to false, the appointment uses specific start/end times. 
     */
    'isAllDay'?: boolean | null;
    /**
     * Whether this appointment blocks the behandelaar\'s availability. When true, other appointments cannot be booked in the same time slot. When false, this appointment does not block the calendar. 
     */
    'blocksAvailability'?: boolean | null;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). Omitted or null leaves it unchanged — unless appointmentTypeId changes, in which case the new type\'s default is taken. 
     */
    'indirectMinutesBefore'?: number | null;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). Omitted or null leaves it unchanged — unless appointmentTypeId changes, in which case the new type\'s default is taken. 
     */
    'indirectMinutesAfter'?: number | null;
    /**
     * Travel time before the session, in minutes (#4817). Omitted or null leaves it unchanged — unless appointmentTypeId changes, in which case the new type\'s default is taken. 
     */
    'travelMinutesBefore'?: number | null;
    /**
     * Travel time after the session, in minutes (#4817). Omitted or null leaves it unchanged — unless appointmentTypeId changes, in which case the new type\'s default is taken. 
     */
    'travelMinutesAfter'?: number | null;
}


/**
 * Request to update all future instances in a recurring series. Either \'updates\' or \'recurrence\' must be provided. 
 */
export interface UpdateAppointmentSeriesRequest {
    /**
     * Apply updates to instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
     */
    'fromDate': string;
    'updates'?: UpdateAppointmentSeriesRequestUpdates;
    /**
     * New recurrence pattern to apply. Replaces the existing pattern for future instances. Existing instances before fromDate are preserved. 
     */
    'recurrence'?: RecurrencePattern | null;
}
/**
 * Fields to update on all affected instances. Same structure as UpdateAppointmentRequest. 
 */
export interface UpdateAppointmentSeriesRequestUpdates {
    /**
     * New title for all instances
     */
    'title'?: string | null;
    /**
     * New description for all instances
     */
    'description'?: string | null;
    /**
     * New start time (milliseconds since epoch). The time-of-day is applied to all instances while preserving their respective dates. 
     */
    'startTime'?: number | null;
    /**
     * New end time (milliseconds since epoch). The time-of-day is applied to all instances. 
     */
    'endTime'?: number | null;
    'locationType'?: LocationType;
    /**
     * New physical location ID
     */
    'locationId'?: string | null;
    /**
     * New room ID
     */
    'roomId'?: string | null;
    /**
     * New URL for online meetings
     */
    'onlineMeetingUrl'?: string | null;
    /**
     * New primary behandelaar user ID
     */
    'behandelaarId'?: string | null;
}


/**
 * Per-participant afmeld-uitkomst (#3835). Records what happened to **one** client; the other participants and the appointment\'s own status are untouched. 
 */
export interface UpdateClientOutcomeRequest {
    'outcome': ParticipantOutcomeChoice;
    'cancellationStatus'?: CancellationStatus;
    /**
     * Actual arrival (epoch ms); null clears back to the scheduled time
     */
    'actualStartTime'?: number | null;
    /**
     * Actual departure (epoch ms); null clears back to the scheduled time
     */
    'actualEndTime'?: number | null;
    /**
     * Answer to the \"vervangen door een andere afspraak?\" question (#3438, #3635), asked for this participant. The replacement is an **individual** appointment for this client — a follow-up phone call is not a group session — even when the original was a group appointment. Only meaningful when the appointment\'s type offers a replacement for the resulting outcome; requesting it otherwise, or for an outcome of PRESENT, is a 400. 
     */
    'createReplacement'?: boolean | null;
}



/**
 * AgendaDayNotesApi - axios parameter creator
 */
export const AgendaDayNotesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Writes a note on one day. Nothing else is recorded: a dagnotitie names no cliënt, no behandelaar and no afspraaktype, and declares nothing (#4063). 
         * @summary Create a dagnotitie
         * @param {CreateAgendaDayNoteRequest} createAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAgendaDayNote: async (createAgendaDayNoteRequest: CreateAgendaDayNoteRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createAgendaDayNoteRequest' is not null or undefined
            assertParamExists('createAgendaDayNote', 'createAgendaDayNoteRequest', createAgendaDayNoteRequest)
            const localVarPath = `/api/v1/agenda/day-notes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createAgendaDayNoteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the note. Nothing references it, so nothing is left behind (#4063).
         * @summary Delete a dagnotitie
         * @param {string} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAgendaDayNote: async (noteId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'noteId' is not null or undefined
            assertParamExists('deleteAgendaDayNote', 'noteId', noteId)
            const localVarPath = `/api/v1/agenda/day-notes/{noteId}`
                .replace(`{${"noteId"}}`, encodeURIComponent(String(noteId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The organization\'s dagnotities whose date falls within `from`..`to`, both inclusive, oldest first (#4063).  This is the one call the agenda makes per view: a dagnotitie is not an afspraak, so it is not returned by `GET /api/v1/appointments` and is invisible to billing and to signals. The list is deliberately unpaged — a calendar view spans days, not pages. 
         * @summary List dagnotities in a date range
         * @param {string} from First day of the range (inclusive)
         * @param {string} to Last day of the range (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaDayNotes: async (from: string, to: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'from' is not null or undefined
            assertParamExists('listAgendaDayNotes', 'from', from)
            // verify required parameter 'to' is not null or undefined
            assertParamExists('listAgendaDayNotes', 'to', to)
            const localVarPath = `/api/v1/agenda/day-notes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces the day and the text of an existing dagnotitie. Both fields are required: a dagnotitie has only these two, so a partial update would be a second spelling of the same call (#4063). 
         * @summary Update a dagnotitie
         * @param {string} noteId 
         * @param {UpdateAgendaDayNoteRequest} updateAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAgendaDayNote: async (noteId: string, updateAgendaDayNoteRequest: UpdateAgendaDayNoteRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'noteId' is not null or undefined
            assertParamExists('updateAgendaDayNote', 'noteId', noteId)
            // verify required parameter 'updateAgendaDayNoteRequest' is not null or undefined
            assertParamExists('updateAgendaDayNote', 'updateAgendaDayNoteRequest', updateAgendaDayNoteRequest)
            const localVarPath = `/api/v1/agenda/day-notes/{noteId}`
                .replace(`{${"noteId"}}`, encodeURIComponent(String(noteId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAgendaDayNoteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AgendaDayNotesApi - functional programming interface
 */
export const AgendaDayNotesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AgendaDayNotesApiAxiosParamCreator(configuration)
    return {
        /**
         * Writes a note on one day. Nothing else is recorded: a dagnotitie names no cliënt, no behandelaar and no afspraaktype, and declares nothing (#4063). 
         * @summary Create a dagnotitie
         * @param {CreateAgendaDayNoteRequest} createAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAgendaDayNote(createAgendaDayNoteRequest: CreateAgendaDayNoteRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaDayNoteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAgendaDayNote(createAgendaDayNoteRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaDayNotesApi.createAgendaDayNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the note. Nothing references it, so nothing is left behind (#4063).
         * @summary Delete a dagnotitie
         * @param {string} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAgendaDayNote(noteId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAgendaDayNote(noteId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaDayNotesApi.deleteAgendaDayNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The organization\'s dagnotities whose date falls within `from`..`to`, both inclusive, oldest first (#4063).  This is the one call the agenda makes per view: a dagnotitie is not an afspraak, so it is not returned by `GET /api/v1/appointments` and is invisible to billing and to signals. The list is deliberately unpaged — a calendar view spans days, not pages. 
         * @summary List dagnotities in a date range
         * @param {string} from First day of the range (inclusive)
         * @param {string} to Last day of the range (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAgendaDayNotes(from: string, to: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AgendaDayNoteResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAgendaDayNotes(from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaDayNotesApi.listAgendaDayNotes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces the day and the text of an existing dagnotitie. Both fields are required: a dagnotitie has only these two, so a partial update would be a second spelling of the same call (#4063). 
         * @summary Update a dagnotitie
         * @param {string} noteId 
         * @param {UpdateAgendaDayNoteRequest} updateAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAgendaDayNote(noteId: string, updateAgendaDayNoteRequest: UpdateAgendaDayNoteRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaDayNoteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAgendaDayNote(noteId, updateAgendaDayNoteRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaDayNotesApi.updateAgendaDayNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AgendaDayNotesApi - factory interface
 */
export const AgendaDayNotesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AgendaDayNotesApiFp(configuration)
    return {
        /**
         * Writes a note on one day. Nothing else is recorded: a dagnotitie names no cliënt, no behandelaar and no afspraaktype, and declares nothing (#4063). 
         * @summary Create a dagnotitie
         * @param {CreateAgendaDayNoteRequest} createAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAgendaDayNote(createAgendaDayNoteRequest: CreateAgendaDayNoteRequest, options?: RawAxiosRequestConfig): AxiosPromise<AgendaDayNoteResponse> {
            return localVarFp.createAgendaDayNote(createAgendaDayNoteRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the note. Nothing references it, so nothing is left behind (#4063).
         * @summary Delete a dagnotitie
         * @param {string} noteId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAgendaDayNote(noteId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAgendaDayNote(noteId, options).then((request) => request(axios, basePath));
        },
        /**
         * The organization\'s dagnotities whose date falls within `from`..`to`, both inclusive, oldest first (#4063).  This is the one call the agenda makes per view: a dagnotitie is not an afspraak, so it is not returned by `GET /api/v1/appointments` and is invisible to billing and to signals. The list is deliberately unpaged — a calendar view spans days, not pages. 
         * @summary List dagnotities in a date range
         * @param {string} from First day of the range (inclusive)
         * @param {string} to Last day of the range (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaDayNotes(from: string, to: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<AgendaDayNoteResponse>> {
            return localVarFp.listAgendaDayNotes(from, to, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces the day and the text of an existing dagnotitie. Both fields are required: a dagnotitie has only these two, so a partial update would be a second spelling of the same call (#4063). 
         * @summary Update a dagnotitie
         * @param {string} noteId 
         * @param {UpdateAgendaDayNoteRequest} updateAgendaDayNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAgendaDayNote(noteId: string, updateAgendaDayNoteRequest: UpdateAgendaDayNoteRequest, options?: RawAxiosRequestConfig): AxiosPromise<AgendaDayNoteResponse> {
            return localVarFp.updateAgendaDayNote(noteId, updateAgendaDayNoteRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AgendaDayNotesApi - object-oriented interface
 */
export class AgendaDayNotesApi extends BaseAPI {
    /**
     * Writes a note on one day. Nothing else is recorded: a dagnotitie names no cliënt, no behandelaar and no afspraaktype, and declares nothing (#4063). 
     * @summary Create a dagnotitie
     * @param {CreateAgendaDayNoteRequest} createAgendaDayNoteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAgendaDayNote(createAgendaDayNoteRequest: CreateAgendaDayNoteRequest, options?: RawAxiosRequestConfig) {
        return AgendaDayNotesApiFp(this.configuration).createAgendaDayNote(createAgendaDayNoteRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the note. Nothing references it, so nothing is left behind (#4063).
     * @summary Delete a dagnotitie
     * @param {string} noteId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAgendaDayNote(noteId: string, options?: RawAxiosRequestConfig) {
        return AgendaDayNotesApiFp(this.configuration).deleteAgendaDayNote(noteId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The organization\'s dagnotities whose date falls within `from`..`to`, both inclusive, oldest first (#4063).  This is the one call the agenda makes per view: a dagnotitie is not an afspraak, so it is not returned by `GET /api/v1/appointments` and is invisible to billing and to signals. The list is deliberately unpaged — a calendar view spans days, not pages. 
     * @summary List dagnotities in a date range
     * @param {string} from First day of the range (inclusive)
     * @param {string} to Last day of the range (inclusive)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAgendaDayNotes(from: string, to: string, options?: RawAxiosRequestConfig) {
        return AgendaDayNotesApiFp(this.configuration).listAgendaDayNotes(from, to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces the day and the text of an existing dagnotitie. Both fields are required: a dagnotitie has only these two, so a partial update would be a second spelling of the same call (#4063). 
     * @summary Update a dagnotitie
     * @param {string} noteId 
     * @param {UpdateAgendaDayNoteRequest} updateAgendaDayNoteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAgendaDayNote(noteId: string, updateAgendaDayNoteRequest: UpdateAgendaDayNoteRequest, options?: RawAxiosRequestConfig) {
        return AgendaDayNotesApiFp(this.configuration).updateAgendaDayNote(noteId, updateAgendaDayNoteRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AgendaMembersApi - axios parameter creator
 */
export const AgendaMembersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the organization members whose agenda the current user may view in the combined agenda, scoped by role like `listSelectableMembers`: - Regular behandelaar: Only themselves - Team lead: Their team members who have appointments (plus themselves) - Administrator: Every member who has appointments in the organization  Unlike `listSelectableMembers` (which drives appointment creation), this endpoint is read-only and only determines who can be shown as a column in the combined agenda: it lists the enabled behandelaren plus the administrative staff who actually participate in appointments (#2918), rather than everyone who could be put on one. 
         * @summary List organization members selectable in the combined agenda
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaMembers: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/appointments/agenda-members`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the organization members the current user may put on an appointment, based on their role: - Regular member: Only themselves - Team lead: All enabled members of their team(s) - Administrator: All enabled members of the organization  **Not** restricted to behandelaren (#3086): administrative staff plan and attend appointments too. The professiecode guardrail is enforced per appointment type when the appointment is saved, not by hiding people here, so an appointment type that requires a profession code still rejects a member without a matching one. Each option carries `isBehandelaar` so the UI can tell the two apart. 
         * @summary List organization members selectable when creating an appointment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSelectableMembers: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/appointments/selectable-members`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AgendaMembersApi - functional programming interface
 */
export const AgendaMembersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AgendaMembersApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the organization members whose agenda the current user may view in the combined agenda, scoped by role like `listSelectableMembers`: - Regular behandelaar: Only themselves - Team lead: Their team members who have appointments (plus themselves) - Administrator: Every member who has appointments in the organization  Unlike `listSelectableMembers` (which drives appointment creation), this endpoint is read-only and only determines who can be shown as a column in the combined agenda: it lists the enabled behandelaren plus the administrative staff who actually participate in appointments (#2918), rather than everyone who could be put on one. 
         * @summary List organization members selectable in the combined agenda
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAgendaMembers(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<OrganizationMemberOption>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAgendaMembers(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaMembersApi.listAgendaMembers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the organization members the current user may put on an appointment, based on their role: - Regular member: Only themselves - Team lead: All enabled members of their team(s) - Administrator: All enabled members of the organization  **Not** restricted to behandelaren (#3086): administrative staff plan and attend appointments too. The professiecode guardrail is enforced per appointment type when the appointment is saved, not by hiding people here, so an appointment type that requires a profession code still rejects a member without a matching one. Each option carries `isBehandelaar` so the UI can tell the two apart. 
         * @summary List organization members selectable when creating an appointment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSelectableMembers(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<OrganizationMemberOption>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSelectableMembers(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AgendaMembersApi.listSelectableMembers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AgendaMembersApi - factory interface
 */
export const AgendaMembersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AgendaMembersApiFp(configuration)
    return {
        /**
         * Returns the organization members whose agenda the current user may view in the combined agenda, scoped by role like `listSelectableMembers`: - Regular behandelaar: Only themselves - Team lead: Their team members who have appointments (plus themselves) - Administrator: Every member who has appointments in the organization  Unlike `listSelectableMembers` (which drives appointment creation), this endpoint is read-only and only determines who can be shown as a column in the combined agenda: it lists the enabled behandelaren plus the administrative staff who actually participate in appointments (#2918), rather than everyone who could be put on one. 
         * @summary List organization members selectable in the combined agenda
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAgendaMembers(options?: RawAxiosRequestConfig): AxiosPromise<Array<OrganizationMemberOption>> {
            return localVarFp.listAgendaMembers(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the organization members the current user may put on an appointment, based on their role: - Regular member: Only themselves - Team lead: All enabled members of their team(s) - Administrator: All enabled members of the organization  **Not** restricted to behandelaren (#3086): administrative staff plan and attend appointments too. The professiecode guardrail is enforced per appointment type when the appointment is saved, not by hiding people here, so an appointment type that requires a profession code still rejects a member without a matching one. Each option carries `isBehandelaar` so the UI can tell the two apart. 
         * @summary List organization members selectable when creating an appointment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSelectableMembers(options?: RawAxiosRequestConfig): AxiosPromise<Array<OrganizationMemberOption>> {
            return localVarFp.listSelectableMembers(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AgendaMembersApi - object-oriented interface
 */
export class AgendaMembersApi extends BaseAPI {
    /**
     * Returns the organization members whose agenda the current user may view in the combined agenda, scoped by role like `listSelectableMembers`: - Regular behandelaar: Only themselves - Team lead: Their team members who have appointments (plus themselves) - Administrator: Every member who has appointments in the organization  Unlike `listSelectableMembers` (which drives appointment creation), this endpoint is read-only and only determines who can be shown as a column in the combined agenda: it lists the enabled behandelaren plus the administrative staff who actually participate in appointments (#2918), rather than everyone who could be put on one. 
     * @summary List organization members selectable in the combined agenda
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAgendaMembers(options?: RawAxiosRequestConfig) {
        return AgendaMembersApiFp(this.configuration).listAgendaMembers(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the organization members the current user may put on an appointment, based on their role: - Regular member: Only themselves - Team lead: All enabled members of their team(s) - Administrator: All enabled members of the organization  **Not** restricted to behandelaren (#3086): administrative staff plan and attend appointments too. The professiecode guardrail is enforced per appointment type when the appointment is saved, not by hiding people here, so an appointment type that requires a profession code still rejects a member without a matching one. Each option carries `isBehandelaar` so the UI can tell the two apart. 
     * @summary List organization members selectable when creating an appointment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSelectableMembers(options?: RawAxiosRequestConfig) {
        return AgendaMembersApiFp(this.configuration).listSelectableMembers(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentSeriesApi - axios parameter creator
 */
export const AppointmentSeriesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Cancels all appointments in a recurring series from the specified date forward. This is a soft-delete operation that sets the status to CANCELLED instead of hard-deleting the appointments.  Each instance\'s cancellation type (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) is determined based on the timing relative to that specific instance.  **Behavior:** - Instances before `fromDate` remain unchanged - Instances on or after `fromDate` are cancelled (status → CANCELLED) - Already cancelled or completed instances are skipped - Cancelled instances remain visible in the system (soft delete) - Audit log captures cancellation with reason for each instance  To cancel a single instance without affecting the series, use the regular POST /appointments/{id}/cancel endpoint. 
         * @summary Cancel this and all following instances in a series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {CancelSeriesRequest} cancelSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointmentSeries: async (appointmentId: string, cancelSeriesRequest: CancelSeriesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('cancelAppointmentSeries', 'appointmentId', appointmentId)
            // verify required parameter 'cancelSeriesRequest' is not null or undefined
            assertParamExists('cancelAppointmentSeries', 'cancelSeriesRequest', cancelSeriesRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/series/cancel`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(cancelSeriesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes all future instances in a recurring series starting from a given date. Past instances are preserved.  If no fromDate is provided, deletes all instances including the series parent. If fromDate equals the series parent\'s date, the entire series is deleted.  To delete a single instance without affecting the series, use the regular DELETE /appointments/{id} endpoint. 
         * @summary Delete all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Delete instances on or after this date. If not provided, deletes the entire series. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointmentSeries: async (appointmentId: string, fromDate?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('deleteAppointmentSeries', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/series`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (fromDate !== undefined) {
                localVarQueryParameter['fromDate'] = (fromDate as any instanceof Date) ?
                    (fromDate as any).toISOString().substring(0,10) :
                    fromDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Detaches a single appointment from its recurring series, making it a standalone appointment. Any occurrence detaches, the series parent (the first occurrence) included.  This operation: - Sets seriesId to null on the appointment - The detached appointment becomes a standalone appointment - The detached appointment no longer shows the recurring indicator - Other occurrences in the series remain unchanged - The series continues normally (no gap created) - Audit log captures the detachment  When the series parent detaches, the earliest remaining occurrence becomes the new parent and carries the recurrence pattern, so the series keeps generating under a new series ID. A series whose only occurrence is its parent has no successor: it ceases to exist and the parent becomes a standalone appointment.  Use this endpoint when a user wants to edit a single occurrence of a recurring appointment (e.g., change the time for just this Tuesday) without affecting the rest of the series. 
         * @summary Detach appointment from recurring series
         * @param {string} appointmentId Appointment UUID (any occurrence of the series, parent included)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        detachAppointmentFromSeries: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('detachAppointmentFromSeries', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/detach`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all appointments in a recurring series. Can be called on either the series parent or any instance. Returns appointments ordered by start time ascending. 
         * @summary Get all instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Filter to only return instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {string} [toDate] Filter to only return instances on or before this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {boolean} [includeParent] Whether to include the series parent in the response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentSeries: async (appointmentId: string, fromDate?: string, toDate?: string, includeParent?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentSeries', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/series`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (fromDate !== undefined) {
                localVarQueryParameter['fromDate'] = (fromDate as any instanceof Date) ?
                    (fromDate as any).toISOString().substring(0,10) :
                    fromDate;
            }

            if (toDate !== undefined) {
                localVarQueryParameter['toDate'] = (toDate as any instanceof Date) ?
                    (toDate as any).toISOString().substring(0,10) :
                    toDate;
            }

            if (includeParent !== undefined) {
                localVarQueryParameter['includeParent'] = includeParent;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates all future instances in a recurring series starting from a given date. Can be used to: - Reschedule all future occurrences (change time but keep recurrence pattern) - Update properties like title, description, location for all future instances - Change the recurrence pattern itself (e.g., from weekly to biweekly)  Updates are applied to instances with startTime >= the specified fromDate. Past instances are not modified.  When updating a single instance (breaking from series), use the regular PUT /appointments/{id} endpoint instead - the instance becomes an \"exception\". 
         * @summary Update all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {UpdateAppointmentSeriesRequest} updateAppointmentSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentSeries: async (appointmentId: string, updateAppointmentSeriesRequest: UpdateAppointmentSeriesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('updateAppointmentSeries', 'appointmentId', appointmentId)
            // verify required parameter 'updateAppointmentSeriesRequest' is not null or undefined
            assertParamExists('updateAppointmentSeries', 'updateAppointmentSeriesRequest', updateAppointmentSeriesRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/series`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAppointmentSeriesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentSeriesApi - functional programming interface
 */
export const AppointmentSeriesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentSeriesApiAxiosParamCreator(configuration)
    return {
        /**
         * Cancels all appointments in a recurring series from the specified date forward. This is a soft-delete operation that sets the status to CANCELLED instead of hard-deleting the appointments.  Each instance\'s cancellation type (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) is determined based on the timing relative to that specific instance.  **Behavior:** - Instances before `fromDate` remain unchanged - Instances on or after `fromDate` are cancelled (status → CANCELLED) - Already cancelled or completed instances are skipped - Cancelled instances remain visible in the system (soft delete) - Audit log captures cancellation with reason for each instance  To cancel a single instance without affecting the series, use the regular POST /appointments/{id}/cancel endpoint. 
         * @summary Cancel this and all following instances in a series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {CancelSeriesRequest} cancelSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelAppointmentSeries(appointmentId: string, cancelSeriesRequest: CancelSeriesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CancelSeriesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelAppointmentSeries(appointmentId, cancelSeriesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentSeriesApi.cancelAppointmentSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes all future instances in a recurring series starting from a given date. Past instances are preserved.  If no fromDate is provided, deletes all instances including the series parent. If fromDate equals the series parent\'s date, the entire series is deleted.  To delete a single instance without affecting the series, use the regular DELETE /appointments/{id} endpoint. 
         * @summary Delete all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Delete instances on or after this date. If not provided, deletes the entire series. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAppointmentSeries(appointmentId: string, fromDate?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeleteSeriesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAppointmentSeries(appointmentId, fromDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentSeriesApi.deleteAppointmentSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Detaches a single appointment from its recurring series, making it a standalone appointment. Any occurrence detaches, the series parent (the first occurrence) included.  This operation: - Sets seriesId to null on the appointment - The detached appointment becomes a standalone appointment - The detached appointment no longer shows the recurring indicator - Other occurrences in the series remain unchanged - The series continues normally (no gap created) - Audit log captures the detachment  When the series parent detaches, the earliest remaining occurrence becomes the new parent and carries the recurrence pattern, so the series keeps generating under a new series ID. A series whose only occurrence is its parent has no successor: it ceases to exist and the parent becomes a standalone appointment.  Use this endpoint when a user wants to edit a single occurrence of a recurring appointment (e.g., change the time for just this Tuesday) without affecting the rest of the series. 
         * @summary Detach appointment from recurring series
         * @param {string} appointmentId Appointment UUID (any occurrence of the series, parent included)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async detachAppointmentFromSeries(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.detachAppointmentFromSeries(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentSeriesApi.detachAppointmentFromSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all appointments in a recurring series. Can be called on either the series parent or any instance. Returns appointments ordered by start time ascending. 
         * @summary Get all instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Filter to only return instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {string} [toDate] Filter to only return instances on or before this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {boolean} [includeParent] Whether to include the series parent in the response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentSeries(appointmentId: string, fromDate?: string, toDate?: string, includeParent?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentSeriesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentSeries(appointmentId, fromDate, toDate, includeParent, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentSeriesApi.getAppointmentSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates all future instances in a recurring series starting from a given date. Can be used to: - Reschedule all future occurrences (change time but keep recurrence pattern) - Update properties like title, description, location for all future instances - Change the recurrence pattern itself (e.g., from weekly to biweekly)  Updates are applied to instances with startTime >= the specified fromDate. Past instances are not modified.  When updating a single instance (breaking from series), use the regular PUT /appointments/{id} endpoint instead - the instance becomes an \"exception\". 
         * @summary Update all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {UpdateAppointmentSeriesRequest} updateAppointmentSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAppointmentSeries(appointmentId: string, updateAppointmentSeriesRequest: UpdateAppointmentSeriesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentSeriesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAppointmentSeries(appointmentId, updateAppointmentSeriesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentSeriesApi.updateAppointmentSeries']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentSeriesApi - factory interface
 */
export const AppointmentSeriesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentSeriesApiFp(configuration)
    return {
        /**
         * Cancels all appointments in a recurring series from the specified date forward. This is a soft-delete operation that sets the status to CANCELLED instead of hard-deleting the appointments.  Each instance\'s cancellation type (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) is determined based on the timing relative to that specific instance.  **Behavior:** - Instances before `fromDate` remain unchanged - Instances on or after `fromDate` are cancelled (status → CANCELLED) - Already cancelled or completed instances are skipped - Cancelled instances remain visible in the system (soft delete) - Audit log captures cancellation with reason for each instance  To cancel a single instance without affecting the series, use the regular POST /appointments/{id}/cancel endpoint. 
         * @summary Cancel this and all following instances in a series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {CancelSeriesRequest} cancelSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointmentSeries(appointmentId: string, cancelSeriesRequest: CancelSeriesRequest, options?: RawAxiosRequestConfig): AxiosPromise<CancelSeriesResponse> {
            return localVarFp.cancelAppointmentSeries(appointmentId, cancelSeriesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes all future instances in a recurring series starting from a given date. Past instances are preserved.  If no fromDate is provided, deletes all instances including the series parent. If fromDate equals the series parent\'s date, the entire series is deleted.  To delete a single instance without affecting the series, use the regular DELETE /appointments/{id} endpoint. 
         * @summary Delete all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Delete instances on or after this date. If not provided, deletes the entire series. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointmentSeries(appointmentId: string, fromDate?: string, options?: RawAxiosRequestConfig): AxiosPromise<DeleteSeriesResponse> {
            return localVarFp.deleteAppointmentSeries(appointmentId, fromDate, options).then((request) => request(axios, basePath));
        },
        /**
         * Detaches a single appointment from its recurring series, making it a standalone appointment. Any occurrence detaches, the series parent (the first occurrence) included.  This operation: - Sets seriesId to null on the appointment - The detached appointment becomes a standalone appointment - The detached appointment no longer shows the recurring indicator - Other occurrences in the series remain unchanged - The series continues normally (no gap created) - Audit log captures the detachment  When the series parent detaches, the earliest remaining occurrence becomes the new parent and carries the recurrence pattern, so the series keeps generating under a new series ID. A series whose only occurrence is its parent has no successor: it ceases to exist and the parent becomes a standalone appointment.  Use this endpoint when a user wants to edit a single occurrence of a recurring appointment (e.g., change the time for just this Tuesday) without affecting the rest of the series. 
         * @summary Detach appointment from recurring series
         * @param {string} appointmentId Appointment UUID (any occurrence of the series, parent included)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        detachAppointmentFromSeries(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.detachAppointmentFromSeries(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all appointments in a recurring series. Can be called on either the series parent or any instance. Returns appointments ordered by start time ascending. 
         * @summary Get all instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {string} [fromDate] Filter to only return instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {string} [toDate] Filter to only return instances on or before this date. Format: ISO 8601 date (YYYY-MM-DD). 
         * @param {boolean} [includeParent] Whether to include the series parent in the response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentSeries(appointmentId: string, fromDate?: string, toDate?: string, includeParent?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentSeriesResponse> {
            return localVarFp.getAppointmentSeries(appointmentId, fromDate, toDate, includeParent, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates all future instances in a recurring series starting from a given date. Can be used to: - Reschedule all future occurrences (change time but keep recurrence pattern) - Update properties like title, description, location for all future instances - Change the recurrence pattern itself (e.g., from weekly to biweekly)  Updates are applied to instances with startTime >= the specified fromDate. Past instances are not modified.  When updating a single instance (breaking from series), use the regular PUT /appointments/{id} endpoint instead - the instance becomes an \"exception\". 
         * @summary Update all future instances in a recurring series
         * @param {string} appointmentId Appointment UUID (series parent or any instance)
         * @param {UpdateAppointmentSeriesRequest} updateAppointmentSeriesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentSeries(appointmentId: string, updateAppointmentSeriesRequest: UpdateAppointmentSeriesRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentSeriesResponse> {
            return localVarFp.updateAppointmentSeries(appointmentId, updateAppointmentSeriesRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentSeriesApi - object-oriented interface
 */
export class AppointmentSeriesApi extends BaseAPI {
    /**
     * Cancels all appointments in a recurring series from the specified date forward. This is a soft-delete operation that sets the status to CANCELLED instead of hard-deleting the appointments.  Each instance\'s cancellation type (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) is determined based on the timing relative to that specific instance.  **Behavior:** - Instances before `fromDate` remain unchanged - Instances on or after `fromDate` are cancelled (status → CANCELLED) - Already cancelled or completed instances are skipped - Cancelled instances remain visible in the system (soft delete) - Audit log captures cancellation with reason for each instance  To cancel a single instance without affecting the series, use the regular POST /appointments/{id}/cancel endpoint. 
     * @summary Cancel this and all following instances in a series
     * @param {string} appointmentId Appointment UUID (series parent or any instance)
     * @param {CancelSeriesRequest} cancelSeriesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelAppointmentSeries(appointmentId: string, cancelSeriesRequest: CancelSeriesRequest, options?: RawAxiosRequestConfig) {
        return AppointmentSeriesApiFp(this.configuration).cancelAppointmentSeries(appointmentId, cancelSeriesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes all future instances in a recurring series starting from a given date. Past instances are preserved.  If no fromDate is provided, deletes all instances including the series parent. If fromDate equals the series parent\'s date, the entire series is deleted.  To delete a single instance without affecting the series, use the regular DELETE /appointments/{id} endpoint. 
     * @summary Delete all future instances in a recurring series
     * @param {string} appointmentId Appointment UUID (series parent or any instance)
     * @param {string} [fromDate] Delete instances on or after this date. If not provided, deletes the entire series. Format: ISO 8601 date (YYYY-MM-DD). 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAppointmentSeries(appointmentId: string, fromDate?: string, options?: RawAxiosRequestConfig) {
        return AppointmentSeriesApiFp(this.configuration).deleteAppointmentSeries(appointmentId, fromDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Detaches a single appointment from its recurring series, making it a standalone appointment. Any occurrence detaches, the series parent (the first occurrence) included.  This operation: - Sets seriesId to null on the appointment - The detached appointment becomes a standalone appointment - The detached appointment no longer shows the recurring indicator - Other occurrences in the series remain unchanged - The series continues normally (no gap created) - Audit log captures the detachment  When the series parent detaches, the earliest remaining occurrence becomes the new parent and carries the recurrence pattern, so the series keeps generating under a new series ID. A series whose only occurrence is its parent has no successor: it ceases to exist and the parent becomes a standalone appointment.  Use this endpoint when a user wants to edit a single occurrence of a recurring appointment (e.g., change the time for just this Tuesday) without affecting the rest of the series. 
     * @summary Detach appointment from recurring series
     * @param {string} appointmentId Appointment UUID (any occurrence of the series, parent included)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public detachAppointmentFromSeries(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentSeriesApiFp(this.configuration).detachAppointmentFromSeries(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all appointments in a recurring series. Can be called on either the series parent or any instance. Returns appointments ordered by start time ascending. 
     * @summary Get all instances in a recurring series
     * @param {string} appointmentId Appointment UUID (series parent or any instance)
     * @param {string} [fromDate] Filter to only return instances on or after this date. Format: ISO 8601 date (YYYY-MM-DD). 
     * @param {string} [toDate] Filter to only return instances on or before this date. Format: ISO 8601 date (YYYY-MM-DD). 
     * @param {boolean} [includeParent] Whether to include the series parent in the response
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentSeries(appointmentId: string, fromDate?: string, toDate?: string, includeParent?: boolean, options?: RawAxiosRequestConfig) {
        return AppointmentSeriesApiFp(this.configuration).getAppointmentSeries(appointmentId, fromDate, toDate, includeParent, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates all future instances in a recurring series starting from a given date. Can be used to: - Reschedule all future occurrences (change time but keep recurrence pattern) - Update properties like title, description, location for all future instances - Change the recurrence pattern itself (e.g., from weekly to biweekly)  Updates are applied to instances with startTime >= the specified fromDate. Past instances are not modified.  When updating a single instance (breaking from series), use the regular PUT /appointments/{id} endpoint instead - the instance becomes an \"exception\". 
     * @summary Update all future instances in a recurring series
     * @param {string} appointmentId Appointment UUID (series parent or any instance)
     * @param {UpdateAppointmentSeriesRequest} updateAppointmentSeriesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAppointmentSeries(appointmentId: string, updateAppointmentSeriesRequest: UpdateAppointmentSeriesRequest, options?: RawAxiosRequestConfig) {
        return AppointmentSeriesApiFp(this.configuration).updateAppointmentSeries(appointmentId, updateAppointmentSeriesRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentStatusApi - axios parameter creator
 */
export const AppointmentStatusApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Cancels an appointment with a reason.  The behandelaar can classify the cancellation explicitly via `cancellationStatus`. When omitted, the status is derived from timing against the organisation\'s late-cancellation window (default 24 hours): CANCELLED_ADVANCE (at least the window before start), CANCELLED_LATE (inside it) or CANCELLED (at or after the start time). 
         * @summary Cancel appointment
         * @param {string} appointmentId Appointment UUID
         * @param {CancelAppointmentRequest} cancelAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointment: async (appointmentId: string, cancelAppointmentRequest: CancelAppointmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('cancelAppointment', 'appointmentId', appointmentId)
            // verify required parameter 'cancelAppointmentRequest' is not null or undefined
            assertParamExists('cancelAppointment', 'cancelAppointmentRequest', cancelAppointmentRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/cancel`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(cancelAppointmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Marks an appointment as completed. The appointment must be in a valid state (not cancelled, not already completed). 
         * @summary Mark appointment as completed
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeAppointment: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('completeAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/complete`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Marks an appointment as no-show when the client didn\'t show up. Also marks all associated appointment clients as not present. Cannot mark cancelled or already completed appointments as no-show. 
         * @summary Mark appointment as no-show
         * @param {string} appointmentId Appointment UUID
         * @param {NoShowAppointmentRequest} noShowAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAppointmentNoShow: async (appointmentId: string, noShowAppointmentRequest: NoShowAppointmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('markAppointmentNoShow', 'appointmentId', appointmentId)
            // verify required parameter 'noShowAppointmentRequest' is not null or undefined
            assertParamExists('markAppointmentNoShow', 'noShowAppointmentRequest', noShowAppointmentRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/no-show`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(noShowAppointmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reverts an appointment from a terminal state back to SCHEDULED.  Terminal states that can be reverted: - COMPLETED - CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE - NO_SHOW  This clears any cancellation-related data (reason, timestamp, cancelling user). Only the appointment organizer can perform this action. 
         * @summary Revert appointment to scheduled
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertAppointment: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('revertAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/revert`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Per-participant outcome (#3835): aanwezig, tijdig afgemeld, te laat afgemeld, geannuleerd or niet verschenen — recorded for **one** client of the appointment.  The appointment\'s own status is not touched. \"De sessie ging niet door\" is a different statement from \"De Vries kwam niet opdagen\", and on a group session only the second one is being made here; the other participants keep whatever was recorded for them.  `isPresent` is derived from the outcome, so the database re-derives the attended minutes and billable quarters and group billing re-prices the remaining kwartier-slices — a group whose presence drops to one bills as an individual consult. 
         * @summary Record the afmeld-uitkomst of one participant
         * @param {string} appointmentId 
         * @param {string} clientId 
         * @param {UpdateClientOutcomeRequest} updateClientOutcomeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientOutcome: async (appointmentId: string, clientId: string, updateClientOutcomeRequest: UpdateClientOutcomeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('updateClientOutcome', 'appointmentId', appointmentId)
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientOutcome', 'clientId', clientId)
            // verify required parameter 'updateClientOutcomeRequest' is not null or undefined
            assertParamExists('updateClientOutcome', 'updateClientOutcomeRequest', updateClientOutcomeRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/clients/{clientId}/outcome`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)))
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientOutcomeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentStatusApi - functional programming interface
 */
export const AppointmentStatusApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentStatusApiAxiosParamCreator(configuration)
    return {
        /**
         * Cancels an appointment with a reason.  The behandelaar can classify the cancellation explicitly via `cancellationStatus`. When omitted, the status is derived from timing against the organisation\'s late-cancellation window (default 24 hours): CANCELLED_ADVANCE (at least the window before start), CANCELLED_LATE (inside it) or CANCELLED (at or after the start time). 
         * @summary Cancel appointment
         * @param {string} appointmentId Appointment UUID
         * @param {CancelAppointmentRequest} cancelAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelAppointment(appointmentId: string, cancelAppointmentRequest: CancelAppointmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelAppointment(appointmentId, cancelAppointmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentStatusApi.cancelAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Marks an appointment as completed. The appointment must be in a valid state (not cancelled, not already completed). 
         * @summary Mark appointment as completed
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async completeAppointment(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.completeAppointment(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentStatusApi.completeAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Marks an appointment as no-show when the client didn\'t show up. Also marks all associated appointment clients as not present. Cannot mark cancelled or already completed appointments as no-show. 
         * @summary Mark appointment as no-show
         * @param {string} appointmentId Appointment UUID
         * @param {NoShowAppointmentRequest} noShowAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markAppointmentNoShow(appointmentId: string, noShowAppointmentRequest: NoShowAppointmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markAppointmentNoShow(appointmentId, noShowAppointmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentStatusApi.markAppointmentNoShow']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reverts an appointment from a terminal state back to SCHEDULED.  Terminal states that can be reverted: - COMPLETED - CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE - NO_SHOW  This clears any cancellation-related data (reason, timestamp, cancelling user). Only the appointment organizer can perform this action. 
         * @summary Revert appointment to scheduled
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revertAppointment(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revertAppointment(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentStatusApi.revertAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Per-participant outcome (#3835): aanwezig, tijdig afgemeld, te laat afgemeld, geannuleerd or niet verschenen — recorded for **one** client of the appointment.  The appointment\'s own status is not touched. \"De sessie ging niet door\" is a different statement from \"De Vries kwam niet opdagen\", and on a group session only the second one is being made here; the other participants keep whatever was recorded for them.  `isPresent` is derived from the outcome, so the database re-derives the attended minutes and billable quarters and group billing re-prices the remaining kwartier-slices — a group whose presence drops to one bills as an individual consult. 
         * @summary Record the afmeld-uitkomst of one participant
         * @param {string} appointmentId 
         * @param {string} clientId 
         * @param {UpdateClientOutcomeRequest} updateClientOutcomeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientOutcome(appointmentId: string, clientId: string, updateClientOutcomeRequest: UpdateClientOutcomeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientOutcome(appointmentId, clientId, updateClientOutcomeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentStatusApi.updateClientOutcome']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentStatusApi - factory interface
 */
export const AppointmentStatusApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentStatusApiFp(configuration)
    return {
        /**
         * Cancels an appointment with a reason.  The behandelaar can classify the cancellation explicitly via `cancellationStatus`. When omitted, the status is derived from timing against the organisation\'s late-cancellation window (default 24 hours): CANCELLED_ADVANCE (at least the window before start), CANCELLED_LATE (inside it) or CANCELLED (at or after the start time). 
         * @summary Cancel appointment
         * @param {string} appointmentId Appointment UUID
         * @param {CancelAppointmentRequest} cancelAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelAppointment(appointmentId: string, cancelAppointmentRequest: CancelAppointmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.cancelAppointment(appointmentId, cancelAppointmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Marks an appointment as completed. The appointment must be in a valid state (not cancelled, not already completed). 
         * @summary Mark appointment as completed
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeAppointment(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.completeAppointment(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Marks an appointment as no-show when the client didn\'t show up. Also marks all associated appointment clients as not present. Cannot mark cancelled or already completed appointments as no-show. 
         * @summary Mark appointment as no-show
         * @param {string} appointmentId Appointment UUID
         * @param {NoShowAppointmentRequest} noShowAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markAppointmentNoShow(appointmentId: string, noShowAppointmentRequest: NoShowAppointmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.markAppointmentNoShow(appointmentId, noShowAppointmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Reverts an appointment from a terminal state back to SCHEDULED.  Terminal states that can be reverted: - COMPLETED - CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE - NO_SHOW  This clears any cancellation-related data (reason, timestamp, cancelling user). Only the appointment organizer can perform this action. 
         * @summary Revert appointment to scheduled
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revertAppointment(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.revertAppointment(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Per-participant outcome (#3835): aanwezig, tijdig afgemeld, te laat afgemeld, geannuleerd or niet verschenen — recorded for **one** client of the appointment.  The appointment\'s own status is not touched. \"De sessie ging niet door\" is a different statement from \"De Vries kwam niet opdagen\", and on a group session only the second one is being made here; the other participants keep whatever was recorded for them.  `isPresent` is derived from the outcome, so the database re-derives the attended minutes and billable quarters and group billing re-prices the remaining kwartier-slices — a group whose presence drops to one bills as an individual consult. 
         * @summary Record the afmeld-uitkomst of one participant
         * @param {string} appointmentId 
         * @param {string} clientId 
         * @param {UpdateClientOutcomeRequest} updateClientOutcomeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientOutcome(appointmentId: string, clientId: string, updateClientOutcomeRequest: UpdateClientOutcomeRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.updateClientOutcome(appointmentId, clientId, updateClientOutcomeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentStatusApi - object-oriented interface
 */
export class AppointmentStatusApi extends BaseAPI {
    /**
     * Cancels an appointment with a reason.  The behandelaar can classify the cancellation explicitly via `cancellationStatus`. When omitted, the status is derived from timing against the organisation\'s late-cancellation window (default 24 hours): CANCELLED_ADVANCE (at least the window before start), CANCELLED_LATE (inside it) or CANCELLED (at or after the start time). 
     * @summary Cancel appointment
     * @param {string} appointmentId Appointment UUID
     * @param {CancelAppointmentRequest} cancelAppointmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelAppointment(appointmentId: string, cancelAppointmentRequest: CancelAppointmentRequest, options?: RawAxiosRequestConfig) {
        return AppointmentStatusApiFp(this.configuration).cancelAppointment(appointmentId, cancelAppointmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Marks an appointment as completed. The appointment must be in a valid state (not cancelled, not already completed). 
     * @summary Mark appointment as completed
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public completeAppointment(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentStatusApiFp(this.configuration).completeAppointment(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Marks an appointment as no-show when the client didn\'t show up. Also marks all associated appointment clients as not present. Cannot mark cancelled or already completed appointments as no-show. 
     * @summary Mark appointment as no-show
     * @param {string} appointmentId Appointment UUID
     * @param {NoShowAppointmentRequest} noShowAppointmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markAppointmentNoShow(appointmentId: string, noShowAppointmentRequest: NoShowAppointmentRequest, options?: RawAxiosRequestConfig) {
        return AppointmentStatusApiFp(this.configuration).markAppointmentNoShow(appointmentId, noShowAppointmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reverts an appointment from a terminal state back to SCHEDULED.  Terminal states that can be reverted: - COMPLETED - CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE - NO_SHOW  This clears any cancellation-related data (reason, timestamp, cancelling user). Only the appointment organizer can perform this action. 
     * @summary Revert appointment to scheduled
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revertAppointment(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentStatusApiFp(this.configuration).revertAppointment(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Per-participant outcome (#3835): aanwezig, tijdig afgemeld, te laat afgemeld, geannuleerd or niet verschenen — recorded for **one** client of the appointment.  The appointment\'s own status is not touched. \"De sessie ging niet door\" is a different statement from \"De Vries kwam niet opdagen\", and on a group session only the second one is being made here; the other participants keep whatever was recorded for them.  `isPresent` is derived from the outcome, so the database re-derives the attended minutes and billable quarters and group billing re-prices the remaining kwartier-slices — a group whose presence drops to one bills as an individual consult. 
     * @summary Record the afmeld-uitkomst of one participant
     * @param {string} appointmentId 
     * @param {string} clientId 
     * @param {UpdateClientOutcomeRequest} updateClientOutcomeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientOutcome(appointmentId: string, clientId: string, updateClientOutcomeRequest: UpdateClientOutcomeRequest, options?: RawAxiosRequestConfig) {
        return AppointmentStatusApiFp(this.configuration).updateClientOutcome(appointmentId, clientId, updateClientOutcomeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentsApi - axios parameter creator
 */
export const AppointmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new appointment in the authenticated user\'s organization
         * @summary Create new appointment
         * @param {CreateAppointmentRequest} createAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAppointment: async (createAppointmentRequest: CreateAppointmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createAppointmentRequest' is not null or undefined
            assertParamExists('createAppointment', 'createAppointmentRequest', createAppointmentRequest)
            const localVarPath = `/api/v1/appointments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createAppointmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes an appointment. If the appointment is part of a recurring series, only this instance is deleted by default. 
         * @summary Delete appointment
         * @param {string} appointmentId Appointment UUID
         * @param {boolean} [deleteSeries] If true and appointment is recurring, delete entire series
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointment: async (appointmentId: string, deleteSeries?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('deleteAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (deleteSeries !== undefined) {
                localVarQueryParameter['deleteSeries'] = deleteSeries;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Hides a cancelled appointment from the **calling user\'s** persoonlijke agenda, the way Outlook\'s \"Remove from Calendar\" clears a cancelled meeting.  Nothing about the appointment changes: it keeps its status, its cancellation timing and its dossier and billing consequences, and a colleague\'s agenda is unaffected. Reverting the appointment to SCHEDULED clears every dismissal, so an un-cancelled appointment reappears for everyone.  Only a cancelled appointment (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) can be dismissed — this is not a general hide-anything mechanism. Dismissing an appointment that is already dismissed succeeds and changes nothing.  No permission of its own: dismissing changes nothing another user can observe, so being able to read the appointment is the whole authorization rule. 
         * @summary Remove a cancelled appointment from your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dismissAppointment: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('dismissAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/dismissal`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single appointment by ID. User must be in same organization.
         * @summary Get appointment by ID
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentById: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentById', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for a specific appointment. Used by the frontend to determine which actions the current user can perform. 
         * @summary Get appointment capabilities
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCapabilities: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentCapabilities', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/capabilities`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The afspraak\'s dossiernotitie: the one answer of its Dossiernotitie system-form submission, filed on the afspraak for no client, so one shared text for the whole (group) session that every participant\'s dossier shows.  An integrator reads and writes the same submission through the form-submission endpoints; this pair is the app\'s shortcut to it, because the app knows neither the system form\'s id nor its question\'s. 
         * @summary Get the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentDossierNote: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentDossierNote', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/dossier-note`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get upcoming appointments for the authenticated user (as behandelaar). Returns scheduled and confirmed appointments starting from now. Useful for dashboard/overview widgets. 
         * @summary Get upcoming appointments
         * @param {number} [limit] Maximum number of appointments to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUpcomingAppointments: async (limit?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/appointments/upcoming`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List appointments in the authenticated user\'s organization. Supports filtering by date range, client, behandelaar, and status. Optimized for calendar view usage.  Every parameter below is applied, **on both listing paths** — the unpaged calendar range (both `startTime` and `endTime` given) and the paginated listing. Three declared here were not read at all — `status` (#3691), `category` (#3713) and `expand` (#3716) — and each answered as if the caller had asked for nothing, with no error and nothing in the logs. `openapi:query-parameters` now fails a parameter its controller never reads, so a fourth cannot land quietly.  A parameter the controller *does* read can still be dropped one layer down, which is what no guard can see: `behandelaarId`, `teamId`, `locationIds` and `groupId` reached the date-range query and were dropped at the paginated call, so the agenda\'s list mode answered with the whole practice\'s afspraken while its calendar mode answered with the caller\'s own (#3720). A new filter belongs on both paths in the same change.  There is deliberately no `category` filter and no `expand`. Adding a `category` filter back means implementing it on *both* listing paths — the unpaged calendar range and the paginated listing — or it is the same silent half-answer again. Recurring appointments need no `expand`: instances are persisted rows, so the range returns them alongside their series parent like any other appointment.  On the persoonlijke agenda — the unpaged calendar range scoped to the calling user alone, whether by leaving `behandelaarId` off or by naming themselves — appointments the caller has dismissed (`POST /appointments/{id}/dismissal`) are left out. Any wider view keeps them: a dismissal is one user\'s, and hiding a row from a colleague\'s column would be a missed appointment. A `clientId` query is not that view: a client\'s dossier agenda keeps every appointment, whoever dismissed it. 
         * @summary List appointments
         * @param {number} [startTime] Start time filter (milliseconds since epoch, inclusive). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting at or after this moment, with or without a &#x60;clientId&#x60;. 
         * @param {number} [endTime] End time filter (milliseconds since epoch, exclusive for the paginated listing). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting before this moment, with or without a &#x60;clientId&#x60;. 
         * @param {string} [clientId] Filter by client ID
         * @param {string} [behandelaarId] Filter by behandelaar (user) ID. Matches on participation — the behandelaar organizes the appointment or attends it.  Honoured on both listing paths. **When no behandelaar, team or &#x60;clientId&#x60; filter is given at all, the listing is scoped to the calling user**: an unfiltered request answers with your own agenda, not the practice\&#39;s. 
         * @param {Array<string>} [behandelaarIds] Filter by multiple behandelaar (user) IDs. Comma-separated list of UUIDs. Takes precedence over &#x60;behandelaarId&#x60;. 
         * @param {string} [teamId] Filter by team ID (returns appointments for all team members). Takes precedence over &#x60;behandelaarId&#x60; and &#x60;behandelaarIds&#x60;; a team with no members returns nothing. 
         * @param {Array<string>} [teamIds] Filter by multiple team IDs. Comma-separated list of UUIDs. Returns appointments for all members of all specified teams.
         * @param {Array<string>} [locationIds] Filter by location IDs. Comma-separated list of UUIDs. Honoured on both listing paths.
         * @param {Array<AppointmentStatus>} [status] Filter by appointment status. Comma-separated list; an appointment matches when its status is any of them. A cancellation carries its timing in its status (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE), so \&quot;geannuleerd\&quot; is a set rather than a single value.  Honoured on both listing paths: the unpaged calendar range (both startTime and endTime given) and the paginated listing, with or without a &#x60;clientId&#x60;.  The calendar range leaves cancelled appointments out by default. An explicit status decides on its own, so &#x60;status&#x3D;CANCELLED_LATE&#x60; returns those appointments rather than nothing. 
         * @param {boolean} [includeCancelled] Keep cancelled appointments (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) in the unpaged calendar range, which leaves them out by default.  Only that path excludes them: the paginated listing returns every status already, so this parameter changes nothing there. Ignored when &#x60;status&#x60; is given — an explicit status decides on its own. 
         * @param {string} [groupId] Filter by client group ID. Returns appointments assigned to this group. Honoured on both listing paths.
         * @param {number} [page] Page number (0-indexed). Only used when not filtering by a full time range (both startTime and endTime).
         * @param {number} [size] Number of items per page. Only used when not filtering by a full time range (both startTime and endTime).
         * @param {ListAppointmentsOrderEnum} [order] Sort direction on startTime for the paginated listing (ignored by the unpaged calendar range, which is always ascending). Defaults to desc (newest first). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointments: async (startTime?: number, endTime?: number, clientId?: string, behandelaarId?: string, behandelaarIds?: Array<string>, teamId?: string, teamIds?: Array<string>, locationIds?: Array<string>, status?: Array<AppointmentStatus>, includeCancelled?: boolean, groupId?: string, page?: number, size?: number, order?: ListAppointmentsOrderEnum, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/appointments`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (startTime !== undefined) {
                localVarQueryParameter['startTime'] = startTime;
            }

            if (endTime !== undefined) {
                localVarQueryParameter['endTime'] = endTime;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (behandelaarId !== undefined) {
                localVarQueryParameter['behandelaarId'] = behandelaarId;
            }

            if (behandelaarIds) {
                localVarQueryParameter['behandelaarIds'] = behandelaarIds.join(COLLECTION_FORMATS.csv);
            }

            if (teamId !== undefined) {
                localVarQueryParameter['teamId'] = teamId;
            }

            if (teamIds) {
                localVarQueryParameter['teamIds'] = teamIds.join(COLLECTION_FORMATS.csv);
            }

            if (locationIds) {
                localVarQueryParameter['locationIds'] = locationIds.join(COLLECTION_FORMATS.csv);
            }

            if (status) {
                localVarQueryParameter['status'] = status.join(COLLECTION_FORMATS.csv);
            }

            if (includeCancelled !== undefined) {
                localVarQueryParameter['includeCancelled'] = includeCancelled;
            }

            if (groupId !== undefined) {
                localVarQueryParameter['groupId'] = groupId;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (order !== undefined) {
                localVarQueryParameter['order'] = order;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every behandelaar taking part in an afspraak of this client — organiser **or** attendee, the same participation the `behandelaarId` filter of `listAppointments` matches on — resolved to a name, de-duplicated and name-sorted. Backs the behandelaar filter of the Afspraken tab in the client dossier (#4373).  Deliberately unbounded in time and status: the tab pages aankomend and afgelopen independently and offers a status filter, so a name the server left out here would be a filter option the user cannot reach for afspraken the list can still show. Being page-independent is the point — a list derived from the loaded page would change as the user pages.  A behandelaar on the client\'s behandelteam who holds no afspraak is **not** here; that is the `/behandelaars` roster, and the filter offers the union of the two. 
         * @summary List the behandelaren who hold an afspraak with this client
         * @param {string} clientId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientAppointmentBehandelaren: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientAppointmentBehandelaren', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/appointment-behandelaars`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets or clears whether a planned afspraak is reserved, invited or confirmed — see `AppointmentConfirmation`. A null `confirmation` clears it. Allowed to whoever may update the afspraak, and only while it is SCHEDULED; a later cancellation, completion or no-show keeps the stored value without changing it. 
         * @summary Set the bevestiging of a planned appointment
         * @param {string} appointmentId Appointment UUID
         * @param {SetAppointmentConfirmationRequest} setAppointmentConfirmationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAppointmentConfirmation: async (appointmentId: string, setAppointmentConfirmationRequest: SetAppointmentConfirmationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('setAppointmentConfirmation', 'appointmentId', appointmentId)
            // verify required parameter 'setAppointmentConfirmationRequest' is not null or undefined
            assertParamExists('setAppointmentConfirmation', 'setAppointmentConfirmationRequest', setAppointmentConfirmationRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/confirmation`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(setAppointmentConfirmationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Undoes `POST /dismissal` for the calling user. Succeeds and changes nothing when the appointment was not dismissed, so the undo action can be issued without first checking.  Unlike the POST this is not restricted to cancelled appointments: it only ever reveals an appointment, and the undo must keep working when the appointment was reverted in the meantime. 
         * @summary Put a dismissed appointment back on your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        undismissAppointment: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('undismissAppointment', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/dismissal`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing appointment. User must be in same organization. Supports drag-and-drop rescheduling via startTime/endTime updates.  The client selection can be edited via `clientIds` (full-replacement set) for appointments that are not group-driven. Removing a client is refused with 422 when a billing line or a reviewed form submission exists for that client on this appointment; otherwise its pending reminders are cancelled and its empty draft forms are cleaned up.  This endpoint does **not** change the appointment\'s status. Each transition has its own endpoint, its own capability and its own domain event: `/complete`, `/cancel`, `/no-show` and `/revert`. A `status` property was carried on this request until #4169 and was never applied, so a body naming it is refused with 400 rather than accepted and ignored. 
         * @summary Update appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentRequest} updateAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointment: async (appointmentId: string, updateAppointmentRequest: UpdateAppointmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('updateAppointment', 'appointmentId', appointmentId)
            // verify required parameter 'updateAppointmentRequest' is not null or undefined
            assertParamExists('updateAppointment', 'updateAppointmentRequest', updateAppointmentRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAppointmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets the dossiernotitie, whatever the afspraak\'s status: a behandelaar writes it right after voltooien, exactly when the rest of the afspraak locks. Allowed to whoever the `updateNotes` capability names.  A blank or null `note` removes it. An unchanged note writes nothing, so saving it again adds no answer version. A new note starts as a DRAFT submission, like any form submission. 
         * @summary Set the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentDossierNoteRequest} updateAppointmentDossierNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentDossierNote: async (appointmentId: string, updateAppointmentDossierNoteRequest: UpdateAppointmentDossierNoteRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('updateAppointmentDossierNote', 'appointmentId', appointmentId)
            // verify required parameter 'updateAppointmentDossierNoteRequest' is not null or undefined
            assertParamExists('updateAppointmentDossierNote', 'updateAppointmentDossierNoteRequest', updateAppointmentDossierNoteRequest)
            const localVarPath = `/api/v1/appointments/{appointmentId}/dossier-note`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAppointmentDossierNoteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentsApi - functional programming interface
 */
export const AppointmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new appointment in the authenticated user\'s organization
         * @summary Create new appointment
         * @param {CreateAppointmentRequest} createAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAppointment(createAppointmentRequest: CreateAppointmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAppointment(createAppointmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.createAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes an appointment. If the appointment is part of a recurring series, only this instance is deleted by default. 
         * @summary Delete appointment
         * @param {string} appointmentId Appointment UUID
         * @param {boolean} [deleteSeries] If true and appointment is recurring, delete entire series
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAppointment(appointmentId: string, deleteSeries?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAppointment(appointmentId, deleteSeries, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.deleteAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Hides a cancelled appointment from the **calling user\'s** persoonlijke agenda, the way Outlook\'s \"Remove from Calendar\" clears a cancelled meeting.  Nothing about the appointment changes: it keeps its status, its cancellation timing and its dossier and billing consequences, and a colleague\'s agenda is unaffected. Reverting the appointment to SCHEDULED clears every dismissal, so an un-cancelled appointment reappears for everyone.  Only a cancelled appointment (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) can be dismissed — this is not a general hide-anything mechanism. Dismissing an appointment that is already dismissed succeeds and changes nothing.  No permission of its own: dismissing changes nothing another user can observe, so being able to read the appointment is the whole authorization rule. 
         * @summary Remove a cancelled appointment from your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dismissAppointment(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.dismissAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single appointment by ID. User must be in same organization.
         * @summary Get appointment by ID
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentById(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentById(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.getAppointmentById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for a specific appointment. Used by the frontend to determine which actions the current user can perform. 
         * @summary Get appointment capabilities
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentCapabilities(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentCapabilitiesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentCapabilities(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.getAppointmentCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The afspraak\'s dossiernotitie: the one answer of its Dossiernotitie system-form submission, filed on the afspraak for no client, so one shared text for the whole (group) session that every participant\'s dossier shows.  An integrator reads and writes the same submission through the form-submission endpoints; this pair is the app\'s shortcut to it, because the app knows neither the system form\'s id nor its question\'s. 
         * @summary Get the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentDossierNote(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentDossierNoteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentDossierNote(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.getAppointmentDossierNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get upcoming appointments for the authenticated user (as behandelaar). Returns scheduled and confirmed appointments starting from now. Useful for dashboard/overview widgets. 
         * @summary Get upcoming appointments
         * @param {number} [limit] Maximum number of appointments to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUpcomingAppointments(limit?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AppointmentResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUpcomingAppointments(limit, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.getUpcomingAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List appointments in the authenticated user\'s organization. Supports filtering by date range, client, behandelaar, and status. Optimized for calendar view usage.  Every parameter below is applied, **on both listing paths** — the unpaged calendar range (both `startTime` and `endTime` given) and the paginated listing. Three declared here were not read at all — `status` (#3691), `category` (#3713) and `expand` (#3716) — and each answered as if the caller had asked for nothing, with no error and nothing in the logs. `openapi:query-parameters` now fails a parameter its controller never reads, so a fourth cannot land quietly.  A parameter the controller *does* read can still be dropped one layer down, which is what no guard can see: `behandelaarId`, `teamId`, `locationIds` and `groupId` reached the date-range query and were dropped at the paginated call, so the agenda\'s list mode answered with the whole practice\'s afspraken while its calendar mode answered with the caller\'s own (#3720). A new filter belongs on both paths in the same change.  There is deliberately no `category` filter and no `expand`. Adding a `category` filter back means implementing it on *both* listing paths — the unpaged calendar range and the paginated listing — or it is the same silent half-answer again. Recurring appointments need no `expand`: instances are persisted rows, so the range returns them alongside their series parent like any other appointment.  On the persoonlijke agenda — the unpaged calendar range scoped to the calling user alone, whether by leaving `behandelaarId` off or by naming themselves — appointments the caller has dismissed (`POST /appointments/{id}/dismissal`) are left out. Any wider view keeps them: a dismissal is one user\'s, and hiding a row from a colleague\'s column would be a missed appointment. A `clientId` query is not that view: a client\'s dossier agenda keeps every appointment, whoever dismissed it. 
         * @summary List appointments
         * @param {number} [startTime] Start time filter (milliseconds since epoch, inclusive). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting at or after this moment, with or without a &#x60;clientId&#x60;. 
         * @param {number} [endTime] End time filter (milliseconds since epoch, exclusive for the paginated listing). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting before this moment, with or without a &#x60;clientId&#x60;. 
         * @param {string} [clientId] Filter by client ID
         * @param {string} [behandelaarId] Filter by behandelaar (user) ID. Matches on participation — the behandelaar organizes the appointment or attends it.  Honoured on both listing paths. **When no behandelaar, team or &#x60;clientId&#x60; filter is given at all, the listing is scoped to the calling user**: an unfiltered request answers with your own agenda, not the practice\&#39;s. 
         * @param {Array<string>} [behandelaarIds] Filter by multiple behandelaar (user) IDs. Comma-separated list of UUIDs. Takes precedence over &#x60;behandelaarId&#x60;. 
         * @param {string} [teamId] Filter by team ID (returns appointments for all team members). Takes precedence over &#x60;behandelaarId&#x60; and &#x60;behandelaarIds&#x60;; a team with no members returns nothing. 
         * @param {Array<string>} [teamIds] Filter by multiple team IDs. Comma-separated list of UUIDs. Returns appointments for all members of all specified teams.
         * @param {Array<string>} [locationIds] Filter by location IDs. Comma-separated list of UUIDs. Honoured on both listing paths.
         * @param {Array<AppointmentStatus>} [status] Filter by appointment status. Comma-separated list; an appointment matches when its status is any of them. A cancellation carries its timing in its status (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE), so \&quot;geannuleerd\&quot; is a set rather than a single value.  Honoured on both listing paths: the unpaged calendar range (both startTime and endTime given) and the paginated listing, with or without a &#x60;clientId&#x60;.  The calendar range leaves cancelled appointments out by default. An explicit status decides on its own, so &#x60;status&#x3D;CANCELLED_LATE&#x60; returns those appointments rather than nothing. 
         * @param {boolean} [includeCancelled] Keep cancelled appointments (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) in the unpaged calendar range, which leaves them out by default.  Only that path excludes them: the paginated listing returns every status already, so this parameter changes nothing there. Ignored when &#x60;status&#x60; is given — an explicit status decides on its own. 
         * @param {string} [groupId] Filter by client group ID. Returns appointments assigned to this group. Honoured on both listing paths.
         * @param {number} [page] Page number (0-indexed). Only used when not filtering by a full time range (both startTime and endTime).
         * @param {number} [size] Number of items per page. Only used when not filtering by a full time range (both startTime and endTime).
         * @param {ListAppointmentsOrderEnum} [order] Sort direction on startTime for the paginated listing (ignored by the unpaged calendar range, which is always ascending). Defaults to desc (newest first). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointments(startTime?: number, endTime?: number, clientId?: string, behandelaarId?: string, behandelaarIds?: Array<string>, teamId?: string, teamIds?: Array<string>, locationIds?: Array<string>, status?: Array<AppointmentStatus>, includeCancelled?: boolean, groupId?: string, page?: number, size?: number, order?: ListAppointmentsOrderEnum, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointments(startTime, endTime, clientId, behandelaarId, behandelaarIds, teamId, teamIds, locationIds, status, includeCancelled, groupId, page, size, order, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.listAppointments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every behandelaar taking part in an afspraak of this client — organiser **or** attendee, the same participation the `behandelaarId` filter of `listAppointments` matches on — resolved to a name, de-duplicated and name-sorted. Backs the behandelaar filter of the Afspraken tab in the client dossier (#4373).  Deliberately unbounded in time and status: the tab pages aankomend and afgelopen independently and offers a status filter, so a name the server left out here would be a filter option the user cannot reach for afspraken the list can still show. Being page-independent is the point — a list derived from the loaded page would change as the user pages.  A behandelaar on the client\'s behandelteam who holds no afspraak is **not** here; that is the `/behandelaars` roster, and the filter offers the union of the two. 
         * @summary List the behandelaren who hold an afspraak with this client
         * @param {string} clientId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientAppointmentBehandelaren(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AppointmentBehandelaarResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientAppointmentBehandelaren(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.listClientAppointmentBehandelaren']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets or clears whether a planned afspraak is reserved, invited or confirmed — see `AppointmentConfirmation`. A null `confirmation` clears it. Allowed to whoever may update the afspraak, and only while it is SCHEDULED; a later cancellation, completion or no-show keeps the stored value without changing it. 
         * @summary Set the bevestiging of a planned appointment
         * @param {string} appointmentId Appointment UUID
         * @param {SetAppointmentConfirmationRequest} setAppointmentConfirmationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async setAppointmentConfirmation(appointmentId: string, setAppointmentConfirmationRequest: SetAppointmentConfirmationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.setAppointmentConfirmation(appointmentId, setAppointmentConfirmationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.setAppointmentConfirmation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Undoes `POST /dismissal` for the calling user. Succeeds and changes nothing when the appointment was not dismissed, so the undo action can be issued without first checking.  Unlike the POST this is not restricted to cancelled appointments: it only ever reveals an appointment, and the undo must keep working when the appointment was reverted in the meantime. 
         * @summary Put a dismissed appointment back on your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async undismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.undismissAppointment(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.undismissAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing appointment. User must be in same organization. Supports drag-and-drop rescheduling via startTime/endTime updates.  The client selection can be edited via `clientIds` (full-replacement set) for appointments that are not group-driven. Removing a client is refused with 422 when a billing line or a reviewed form submission exists for that client on this appointment; otherwise its pending reminders are cancelled and its empty draft forms are cleaned up.  This endpoint does **not** change the appointment\'s status. Each transition has its own endpoint, its own capability and its own domain event: `/complete`, `/cancel`, `/no-show` and `/revert`. A `status` property was carried on this request until #4169 and was never applied, so a body naming it is refused with 400 rather than accepted and ignored. 
         * @summary Update appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentRequest} updateAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAppointment(appointmentId: string, updateAppointmentRequest: UpdateAppointmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAppointment(appointmentId, updateAppointmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.updateAppointment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets the dossiernotitie, whatever the afspraak\'s status: a behandelaar writes it right after voltooien, exactly when the rest of the afspraak locks. Allowed to whoever the `updateNotes` capability names.  A blank or null `note` removes it. An unchanged note writes nothing, so saving it again adds no answer version. A new note starts as a DRAFT submission, like any form submission. 
         * @summary Set the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentDossierNoteRequest} updateAppointmentDossierNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAppointmentDossierNote(appointmentId: string, updateAppointmentDossierNoteRequest: UpdateAppointmentDossierNoteRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentDossierNoteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAppointmentDossierNote(appointmentId, updateAppointmentDossierNoteRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentsApi.updateAppointmentDossierNote']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentsApi - factory interface
 */
export const AppointmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentsApiFp(configuration)
    return {
        /**
         * Creates a new appointment in the authenticated user\'s organization
         * @summary Create new appointment
         * @param {CreateAppointmentRequest} createAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAppointment(createAppointmentRequest: CreateAppointmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.createAppointment(createAppointmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes an appointment. If the appointment is part of a recurring series, only this instance is deleted by default. 
         * @summary Delete appointment
         * @param {string} appointmentId Appointment UUID
         * @param {boolean} [deleteSeries] If true and appointment is recurring, delete entire series
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointment(appointmentId: string, deleteSeries?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAppointment(appointmentId, deleteSeries, options).then((request) => request(axios, basePath));
        },
        /**
         * Hides a cancelled appointment from the **calling user\'s** persoonlijke agenda, the way Outlook\'s \"Remove from Calendar\" clears a cancelled meeting.  Nothing about the appointment changes: it keeps its status, its cancellation timing and its dossier and billing consequences, and a colleague\'s agenda is unaffected. Reverting the appointment to SCHEDULED clears every dismissal, so an un-cancelled appointment reappears for everyone.  Only a cancelled appointment (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) can be dismissed — this is not a general hide-anything mechanism. Dismissing an appointment that is already dismissed succeeds and changes nothing.  No permission of its own: dismissing changes nothing another user can observe, so being able to read the appointment is the whole authorization rule. 
         * @summary Remove a cancelled appointment from your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.dismissAppointment(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single appointment by ID. User must be in same organization.
         * @summary Get appointment by ID
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentById(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.getAppointmentById(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for a specific appointment. Used by the frontend to determine which actions the current user can perform. 
         * @summary Get appointment capabilities
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentCapabilities(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentCapabilitiesResponse> {
            return localVarFp.getAppointmentCapabilities(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * The afspraak\'s dossiernotitie: the one answer of its Dossiernotitie system-form submission, filed on the afspraak for no client, so one shared text for the whole (group) session that every participant\'s dossier shows.  An integrator reads and writes the same submission through the form-submission endpoints; this pair is the app\'s shortcut to it, because the app knows neither the system form\'s id nor its question\'s. 
         * @summary Get the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentDossierNote(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentDossierNoteResponse> {
            return localVarFp.getAppointmentDossierNote(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get upcoming appointments for the authenticated user (as behandelaar). Returns scheduled and confirmed appointments starting from now. Useful for dashboard/overview widgets. 
         * @summary Get upcoming appointments
         * @param {number} [limit] Maximum number of appointments to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUpcomingAppointments(limit?: number, options?: RawAxiosRequestConfig): AxiosPromise<Array<AppointmentResponse>> {
            return localVarFp.getUpcomingAppointments(limit, options).then((request) => request(axios, basePath));
        },
        /**
         * List appointments in the authenticated user\'s organization. Supports filtering by date range, client, behandelaar, and status. Optimized for calendar view usage.  Every parameter below is applied, **on both listing paths** — the unpaged calendar range (both `startTime` and `endTime` given) and the paginated listing. Three declared here were not read at all — `status` (#3691), `category` (#3713) and `expand` (#3716) — and each answered as if the caller had asked for nothing, with no error and nothing in the logs. `openapi:query-parameters` now fails a parameter its controller never reads, so a fourth cannot land quietly.  A parameter the controller *does* read can still be dropped one layer down, which is what no guard can see: `behandelaarId`, `teamId`, `locationIds` and `groupId` reached the date-range query and were dropped at the paginated call, so the agenda\'s list mode answered with the whole practice\'s afspraken while its calendar mode answered with the caller\'s own (#3720). A new filter belongs on both paths in the same change.  There is deliberately no `category` filter and no `expand`. Adding a `category` filter back means implementing it on *both* listing paths — the unpaged calendar range and the paginated listing — or it is the same silent half-answer again. Recurring appointments need no `expand`: instances are persisted rows, so the range returns them alongside their series parent like any other appointment.  On the persoonlijke agenda — the unpaged calendar range scoped to the calling user alone, whether by leaving `behandelaarId` off or by naming themselves — appointments the caller has dismissed (`POST /appointments/{id}/dismissal`) are left out. Any wider view keeps them: a dismissal is one user\'s, and hiding a row from a colleague\'s column would be a missed appointment. A `clientId` query is not that view: a client\'s dossier agenda keeps every appointment, whoever dismissed it. 
         * @summary List appointments
         * @param {number} [startTime] Start time filter (milliseconds since epoch, inclusive). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting at or after this moment, with or without a &#x60;clientId&#x60;. 
         * @param {number} [endTime] End time filter (milliseconds since epoch, exclusive for the paginated listing). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting before this moment, with or without a &#x60;clientId&#x60;. 
         * @param {string} [clientId] Filter by client ID
         * @param {string} [behandelaarId] Filter by behandelaar (user) ID. Matches on participation — the behandelaar organizes the appointment or attends it.  Honoured on both listing paths. **When no behandelaar, team or &#x60;clientId&#x60; filter is given at all, the listing is scoped to the calling user**: an unfiltered request answers with your own agenda, not the practice\&#39;s. 
         * @param {Array<string>} [behandelaarIds] Filter by multiple behandelaar (user) IDs. Comma-separated list of UUIDs. Takes precedence over &#x60;behandelaarId&#x60;. 
         * @param {string} [teamId] Filter by team ID (returns appointments for all team members). Takes precedence over &#x60;behandelaarId&#x60; and &#x60;behandelaarIds&#x60;; a team with no members returns nothing. 
         * @param {Array<string>} [teamIds] Filter by multiple team IDs. Comma-separated list of UUIDs. Returns appointments for all members of all specified teams.
         * @param {Array<string>} [locationIds] Filter by location IDs. Comma-separated list of UUIDs. Honoured on both listing paths.
         * @param {Array<AppointmentStatus>} [status] Filter by appointment status. Comma-separated list; an appointment matches when its status is any of them. A cancellation carries its timing in its status (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE), so \&quot;geannuleerd\&quot; is a set rather than a single value.  Honoured on both listing paths: the unpaged calendar range (both startTime and endTime given) and the paginated listing, with or without a &#x60;clientId&#x60;.  The calendar range leaves cancelled appointments out by default. An explicit status decides on its own, so &#x60;status&#x3D;CANCELLED_LATE&#x60; returns those appointments rather than nothing. 
         * @param {boolean} [includeCancelled] Keep cancelled appointments (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) in the unpaged calendar range, which leaves them out by default.  Only that path excludes them: the paginated listing returns every status already, so this parameter changes nothing there. Ignored when &#x60;status&#x60; is given — an explicit status decides on its own. 
         * @param {string} [groupId] Filter by client group ID. Returns appointments assigned to this group. Honoured on both listing paths.
         * @param {number} [page] Page number (0-indexed). Only used when not filtering by a full time range (both startTime and endTime).
         * @param {number} [size] Number of items per page. Only used when not filtering by a full time range (both startTime and endTime).
         * @param {ListAppointmentsOrderEnum} [order] Sort direction on startTime for the paginated listing (ignored by the unpaged calendar range, which is always ascending). Defaults to desc (newest first). 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointments(startTime?: number, endTime?: number, clientId?: string, behandelaarId?: string, behandelaarIds?: Array<string>, teamId?: string, teamIds?: Array<string>, locationIds?: Array<string>, status?: Array<AppointmentStatus>, includeCancelled?: boolean, groupId?: string, page?: number, size?: number, order?: ListAppointmentsOrderEnum, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentListResponse> {
            return localVarFp.listAppointments(startTime, endTime, clientId, behandelaarId, behandelaarIds, teamId, teamIds, locationIds, status, includeCancelled, groupId, page, size, order, options).then((request) => request(axios, basePath));
        },
        /**
         * Every behandelaar taking part in an afspraak of this client — organiser **or** attendee, the same participation the `behandelaarId` filter of `listAppointments` matches on — resolved to a name, de-duplicated and name-sorted. Backs the behandelaar filter of the Afspraken tab in the client dossier (#4373).  Deliberately unbounded in time and status: the tab pages aankomend and afgelopen independently and offers a status filter, so a name the server left out here would be a filter option the user cannot reach for afspraken the list can still show. Being page-independent is the point — a list derived from the loaded page would change as the user pages.  A behandelaar on the client\'s behandelteam who holds no afspraak is **not** here; that is the `/behandelaars` roster, and the filter offers the union of the two. 
         * @summary List the behandelaren who hold an afspraak with this client
         * @param {string} clientId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientAppointmentBehandelaren(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<AppointmentBehandelaarResponse>> {
            return localVarFp.listClientAppointmentBehandelaren(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets or clears whether a planned afspraak is reserved, invited or confirmed — see `AppointmentConfirmation`. A null `confirmation` clears it. Allowed to whoever may update the afspraak, and only while it is SCHEDULED; a later cancellation, completion or no-show keeps the stored value without changing it. 
         * @summary Set the bevestiging of a planned appointment
         * @param {string} appointmentId Appointment UUID
         * @param {SetAppointmentConfirmationRequest} setAppointmentConfirmationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        setAppointmentConfirmation(appointmentId: string, setAppointmentConfirmationRequest: SetAppointmentConfirmationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.setAppointmentConfirmation(appointmentId, setAppointmentConfirmationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Undoes `POST /dismissal` for the calling user. Succeeds and changes nothing when the appointment was not dismissed, so the undo action can be issued without first checking.  Unlike the POST this is not restricted to cancelled appointments: it only ever reveals an appointment, and the undo must keep working when the appointment was reverted in the meantime. 
         * @summary Put a dismissed appointment back on your own agenda
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        undismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.undismissAppointment(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing appointment. User must be in same organization. Supports drag-and-drop rescheduling via startTime/endTime updates.  The client selection can be edited via `clientIds` (full-replacement set) for appointments that are not group-driven. Removing a client is refused with 422 when a billing line or a reviewed form submission exists for that client on this appointment; otherwise its pending reminders are cancelled and its empty draft forms are cleaned up.  This endpoint does **not** change the appointment\'s status. Each transition has its own endpoint, its own capability and its own domain event: `/complete`, `/cancel`, `/no-show` and `/revert`. A `status` property was carried on this request until #4169 and was never applied, so a body naming it is refused with 400 rather than accepted and ignored. 
         * @summary Update appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentRequest} updateAppointmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointment(appointmentId: string, updateAppointmentRequest: UpdateAppointmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentResponse> {
            return localVarFp.updateAppointment(appointmentId, updateAppointmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets the dossiernotitie, whatever the afspraak\'s status: a behandelaar writes it right after voltooien, exactly when the rest of the afspraak locks. Allowed to whoever the `updateNotes` capability names.  A blank or null `note` removes it. An unchanged note writes nothing, so saving it again adds no answer version. A new note starts as a DRAFT submission, like any form submission. 
         * @summary Set the dossiernotitie of an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {UpdateAppointmentDossierNoteRequest} updateAppointmentDossierNoteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentDossierNote(appointmentId: string, updateAppointmentDossierNoteRequest: UpdateAppointmentDossierNoteRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentDossierNoteResponse> {
            return localVarFp.updateAppointmentDossierNote(appointmentId, updateAppointmentDossierNoteRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentsApi - object-oriented interface
 */
export class AppointmentsApi extends BaseAPI {
    /**
     * Creates a new appointment in the authenticated user\'s organization
     * @summary Create new appointment
     * @param {CreateAppointmentRequest} createAppointmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAppointment(createAppointmentRequest: CreateAppointmentRequest, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).createAppointment(createAppointmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes an appointment. If the appointment is part of a recurring series, only this instance is deleted by default. 
     * @summary Delete appointment
     * @param {string} appointmentId Appointment UUID
     * @param {boolean} [deleteSeries] If true and appointment is recurring, delete entire series
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAppointment(appointmentId: string, deleteSeries?: boolean, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).deleteAppointment(appointmentId, deleteSeries, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Hides a cancelled appointment from the **calling user\'s** persoonlijke agenda, the way Outlook\'s \"Remove from Calendar\" clears a cancelled meeting.  Nothing about the appointment changes: it keeps its status, its cancellation timing and its dossier and billing consequences, and a colleague\'s agenda is unaffected. Reverting the appointment to SCHEDULED clears every dismissal, so an un-cancelled appointment reappears for everyone.  Only a cancelled appointment (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) can be dismissed — this is not a general hide-anything mechanism. Dismissing an appointment that is already dismissed succeeds and changes nothing.  No permission of its own: dismissing changes nothing another user can observe, so being able to read the appointment is the whole authorization rule. 
     * @summary Remove a cancelled appointment from your own agenda
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public dismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).dismissAppointment(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single appointment by ID. User must be in same organization.
     * @summary Get appointment by ID
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentById(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).getAppointmentById(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for a specific appointment. Used by the frontend to determine which actions the current user can perform. 
     * @summary Get appointment capabilities
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentCapabilities(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).getAppointmentCapabilities(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The afspraak\'s dossiernotitie: the one answer of its Dossiernotitie system-form submission, filed on the afspraak for no client, so one shared text for the whole (group) session that every participant\'s dossier shows.  An integrator reads and writes the same submission through the form-submission endpoints; this pair is the app\'s shortcut to it, because the app knows neither the system form\'s id nor its question\'s. 
     * @summary Get the dossiernotitie of an appointment
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentDossierNote(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).getAppointmentDossierNote(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get upcoming appointments for the authenticated user (as behandelaar). Returns scheduled and confirmed appointments starting from now. Useful for dashboard/overview widgets. 
     * @summary Get upcoming appointments
     * @param {number} [limit] Maximum number of appointments to return
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUpcomingAppointments(limit?: number, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).getUpcomingAppointments(limit, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List appointments in the authenticated user\'s organization. Supports filtering by date range, client, behandelaar, and status. Optimized for calendar view usage.  Every parameter below is applied, **on both listing paths** — the unpaged calendar range (both `startTime` and `endTime` given) and the paginated listing. Three declared here were not read at all — `status` (#3691), `category` (#3713) and `expand` (#3716) — and each answered as if the caller had asked for nothing, with no error and nothing in the logs. `openapi:query-parameters` now fails a parameter its controller never reads, so a fourth cannot land quietly.  A parameter the controller *does* read can still be dropped one layer down, which is what no guard can see: `behandelaarId`, `teamId`, `locationIds` and `groupId` reached the date-range query and were dropped at the paginated call, so the agenda\'s list mode answered with the whole practice\'s afspraken while its calendar mode answered with the caller\'s own (#3720). A new filter belongs on both paths in the same change.  There is deliberately no `category` filter and no `expand`. Adding a `category` filter back means implementing it on *both* listing paths — the unpaged calendar range and the paginated listing — or it is the same silent half-answer again. Recurring appointments need no `expand`: instances are persisted rows, so the range returns them alongside their series parent like any other appointment.  On the persoonlijke agenda — the unpaged calendar range scoped to the calling user alone, whether by leaving `behandelaarId` off or by naming themselves — appointments the caller has dismissed (`POST /appointments/{id}/dismissal`) are left out. Any wider view keeps them: a dismissal is one user\'s, and hiding a row from a colleague\'s column would be a missed appointment. A `clientId` query is not that view: a client\'s dossier agenda keeps every appointment, whoever dismissed it. 
     * @summary List appointments
     * @param {number} [startTime] Start time filter (milliseconds since epoch, inclusive). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting at or after this moment, with or without a &#x60;clientId&#x60;. 
     * @param {number} [endTime] End time filter (milliseconds since epoch, exclusive for the paginated listing). When both startTime and endTime are given, the response is the unpaged calendar range. Given on its own it bounds the paginated listing to appointments starting before this moment, with or without a &#x60;clientId&#x60;. 
     * @param {string} [clientId] Filter by client ID
     * @param {string} [behandelaarId] Filter by behandelaar (user) ID. Matches on participation — the behandelaar organizes the appointment or attends it.  Honoured on both listing paths. **When no behandelaar, team or &#x60;clientId&#x60; filter is given at all, the listing is scoped to the calling user**: an unfiltered request answers with your own agenda, not the practice\&#39;s. 
     * @param {Array<string>} [behandelaarIds] Filter by multiple behandelaar (user) IDs. Comma-separated list of UUIDs. Takes precedence over &#x60;behandelaarId&#x60;. 
     * @param {string} [teamId] Filter by team ID (returns appointments for all team members). Takes precedence over &#x60;behandelaarId&#x60; and &#x60;behandelaarIds&#x60;; a team with no members returns nothing. 
     * @param {Array<string>} [teamIds] Filter by multiple team IDs. Comma-separated list of UUIDs. Returns appointments for all members of all specified teams.
     * @param {Array<string>} [locationIds] Filter by location IDs. Comma-separated list of UUIDs. Honoured on both listing paths.
     * @param {Array<AppointmentStatus>} [status] Filter by appointment status. Comma-separated list; an appointment matches when its status is any of them. A cancellation carries its timing in its status (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE), so \&quot;geannuleerd\&quot; is a set rather than a single value.  Honoured on both listing paths: the unpaged calendar range (both startTime and endTime given) and the paginated listing, with or without a &#x60;clientId&#x60;.  The calendar range leaves cancelled appointments out by default. An explicit status decides on its own, so &#x60;status&#x3D;CANCELLED_LATE&#x60; returns those appointments rather than nothing. 
     * @param {boolean} [includeCancelled] Keep cancelled appointments (CANCELLED, CANCELLED_ADVANCE, CANCELLED_LATE) in the unpaged calendar range, which leaves them out by default.  Only that path excludes them: the paginated listing returns every status already, so this parameter changes nothing there. Ignored when &#x60;status&#x60; is given — an explicit status decides on its own. 
     * @param {string} [groupId] Filter by client group ID. Returns appointments assigned to this group. Honoured on both listing paths.
     * @param {number} [page] Page number (0-indexed). Only used when not filtering by a full time range (both startTime and endTime).
     * @param {number} [size] Number of items per page. Only used when not filtering by a full time range (both startTime and endTime).
     * @param {ListAppointmentsOrderEnum} [order] Sort direction on startTime for the paginated listing (ignored by the unpaged calendar range, which is always ascending). Defaults to desc (newest first). 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAppointments(startTime?: number, endTime?: number, clientId?: string, behandelaarId?: string, behandelaarIds?: Array<string>, teamId?: string, teamIds?: Array<string>, locationIds?: Array<string>, status?: Array<AppointmentStatus>, includeCancelled?: boolean, groupId?: string, page?: number, size?: number, order?: ListAppointmentsOrderEnum, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).listAppointments(startTime, endTime, clientId, behandelaarId, behandelaarIds, teamId, teamIds, locationIds, status, includeCancelled, groupId, page, size, order, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every behandelaar taking part in an afspraak of this client — organiser **or** attendee, the same participation the `behandelaarId` filter of `listAppointments` matches on — resolved to a name, de-duplicated and name-sorted. Backs the behandelaar filter of the Afspraken tab in the client dossier (#4373).  Deliberately unbounded in time and status: the tab pages aankomend and afgelopen independently and offers a status filter, so a name the server left out here would be a filter option the user cannot reach for afspraken the list can still show. Being page-independent is the point — a list derived from the loaded page would change as the user pages.  A behandelaar on the client\'s behandelteam who holds no afspraak is **not** here; that is the `/behandelaars` roster, and the filter offers the union of the two. 
     * @summary List the behandelaren who hold an afspraak with this client
     * @param {string} clientId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientAppointmentBehandelaren(clientId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).listClientAppointmentBehandelaren(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets or clears whether a planned afspraak is reserved, invited or confirmed — see `AppointmentConfirmation`. A null `confirmation` clears it. Allowed to whoever may update the afspraak, and only while it is SCHEDULED; a later cancellation, completion or no-show keeps the stored value without changing it. 
     * @summary Set the bevestiging of a planned appointment
     * @param {string} appointmentId Appointment UUID
     * @param {SetAppointmentConfirmationRequest} setAppointmentConfirmationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public setAppointmentConfirmation(appointmentId: string, setAppointmentConfirmationRequest: SetAppointmentConfirmationRequest, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).setAppointmentConfirmation(appointmentId, setAppointmentConfirmationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Undoes `POST /dismissal` for the calling user. Succeeds and changes nothing when the appointment was not dismissed, so the undo action can be issued without first checking.  Unlike the POST this is not restricted to cancelled appointments: it only ever reveals an appointment, and the undo must keep working when the appointment was reverted in the meantime. 
     * @summary Put a dismissed appointment back on your own agenda
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public undismissAppointment(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).undismissAppointment(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing appointment. User must be in same organization. Supports drag-and-drop rescheduling via startTime/endTime updates.  The client selection can be edited via `clientIds` (full-replacement set) for appointments that are not group-driven. Removing a client is refused with 422 when a billing line or a reviewed form submission exists for that client on this appointment; otherwise its pending reminders are cancelled and its empty draft forms are cleaned up.  This endpoint does **not** change the appointment\'s status. Each transition has its own endpoint, its own capability and its own domain event: `/complete`, `/cancel`, `/no-show` and `/revert`. A `status` property was carried on this request until #4169 and was never applied, so a body naming it is refused with 400 rather than accepted and ignored. 
     * @summary Update appointment
     * @param {string} appointmentId Appointment UUID
     * @param {UpdateAppointmentRequest} updateAppointmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAppointment(appointmentId: string, updateAppointmentRequest: UpdateAppointmentRequest, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).updateAppointment(appointmentId, updateAppointmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets the dossiernotitie, whatever the afspraak\'s status: a behandelaar writes it right after voltooien, exactly when the rest of the afspraak locks. Allowed to whoever the `updateNotes` capability names.  A blank or null `note` removes it. An unchanged note writes nothing, so saving it again adds no answer version. A new note starts as a DRAFT submission, like any form submission. 
     * @summary Set the dossiernotitie of an appointment
     * @param {string} appointmentId Appointment UUID
     * @param {UpdateAppointmentDossierNoteRequest} updateAppointmentDossierNoteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAppointmentDossierNote(appointmentId: string, updateAppointmentDossierNoteRequest: UpdateAppointmentDossierNoteRequest, options?: RawAxiosRequestConfig) {
        return AppointmentsApiFp(this.configuration).updateAppointmentDossierNote(appointmentId, updateAppointmentDossierNoteRequest, options).then((request) => request(this.axios, this.basePath));
    }
}

export const ListAppointmentsOrderEnum = {
    Asc: 'asc',
    Desc: 'desc'
} as const;
export type ListAppointmentsOrderEnum = typeof ListAppointmentsOrderEnum[keyof typeof ListAppointmentsOrderEnum];


